import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ClipboardList, Plus, Search, Eye, Send, CheckCircle, XCircle, RotateCcw, Users, Clock, FileText, Upload, Download, Award, Paperclip, Star, ArrowLeftRight, MessageSquare, Activity, Trash2, Archive, Copy, Check, Share2, File, Video, Image, ZoomIn, ZoomOut, X, Maximize2, ExternalLink, AlertCircle, Filter } from 'lucide-react';
import { parseISO, isPast, isToday, isTomorrow, formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Modal, LabeledInput } from '../../components/SharedUI';
import {
  getTmTasks, createTmTask, updateTmTask, getTmTask, assignTmTask, distributeTmTask, cancelTmTask, rejectTmTask, reopenTmTask,
  uploadTmAttachment, createTmSubmission, getTmSubmissions,
  reviewTmSubmissionTL, reviewTmSubmissionSV, transferTmSubmission,
  getTmTeams, getTmTeamMembers, archiveTmTask, deleteTmTask,
  TM_STATUS_MAP, TM_STATUS_COLORS, formatIQD, formatSeconds,
  getSupervisorsList, getTmClients, getTmSubscriptions,
  addTmComment, toggleTmChecklist, getDownloadUrl, getZipDownloadUrl, getTmRatePerSecond
} from '../../utils/tm_api';
import { parseArabicToEnglish } from '../../utils/formatters';

// ═══ Priority Config ═══
const PRIORITY_CONFIG = {
  urgent: { label: 'عاجل 🔴', cls: 'bg-red-500/20 text-red-400' },
  high: { label: 'عالية 🟠', cls: 'bg-orange-500/20 text-orange-400' },
  medium: { label: 'متوسطة 🟡', cls: 'bg-yellow-500/20 text-yellow-400' },
  low: { label: 'منخفضة 🔵', cls: 'bg-blue-500/20 text-blue-400' },
};

// ═══ Multiplier Config ═══
const MULTIPLIER_CONFIG = {
  1: { label: '', cls: '' },
  1.5: { label: '✖️1.5', cls: 'bg-sky-500/20 text-sky-400' },
  2: { label: '✖️2 مضاعف', cls: 'bg-green-500/20 text-green-400' },
  2.5: { label: '✖️2.5', cls: 'bg-purple-500/20 text-purple-400' },
  3: { label: '✖️3 ثلاثي ⭐', cls: 'bg-yellow-500/20 text-yellow-300 animate-pulse' },
};

// ═══ Kanban Columns ═══
const KANBAN_COLUMNS = [
  { key: 'todo', label: '📝 للتنفيذ', statuses: ['DRAFT', 'ASSIGNED'], color: 'gray', borderColor: 'border-gray-500' },
  { key: 'progress', label: '⚡ قيد التنفيذ', statuses: ['IN_PROGRESS'], color: 'yellow', borderColor: 'border-yellow-500' },
  { key: 'review', label: '🔍 قيد المراجعة', statuses: ['SUBMITTED', 'TL_APPROVED', 'REVISION'], color: 'purple', borderColor: 'border-purple-500' },
  { key: 'done', label: '✅ التسليمات', statuses: ['ACCEPTED', 'LOST', 'REJECTED', 'CANCELLED'], color: 'green', borderColor: 'border-green-500' },
];

// Helper: Format bytes to human readable size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Helper: Format task ID to code format #MS-XXXX
const formatTaskCode = (id) => `#MS-${String(id).padStart(4, '0')}`;


/**
 * Image Lightbox Modal with Pinch-To-Zoom and Safe Action Area
 */
function FileImageLightbox({ file, onClose }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => {
    setScale(prev => {
      const next = Math.max(prev - 0.5, 1);
      if (next === 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };

  const handleDoubleTap = () => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(2);
    }
  };

  const handleStart = (clientX, clientY) => {
    if (scale === 1) return;
    setIsDragging(true);
    dragStart.current = { x: clientX - position.x, y: clientY - position.y };
  };

  const handleMove = (clientX, clientY) => {
    if (!isDragging) return;
    setPosition({
      x: clientX - dragStart.current.x,
      y: clientY - dragStart.current.y
    });
  };

  const handleEnd = () => setIsDragging(false);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex flex-col justify-between select-none animate-fade-in">
      <div className="flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent text-white z-10">
        <div className="text-right flex-1 min-w-0 pr-2">
          <h4 className="font-bold text-sm truncate">{file.file_name || file.file_path.split('/').pop()}</h4>
          <p className="text-[11px] text-gray-400">{formatFileSize(file.file_size)}</p>
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={handleZoomOut} 
            disabled={scale <= 1}
            className="p-2 hover:bg-white/10 active:bg-white/20 rounded-full transition disabled:opacity-30 text-white"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-xs font-mono w-10 text-center">{scale.toFixed(1)}x</span>
          <button 
            onClick={handleZoomIn} 
            disabled={scale >= 4}
            className="p-2 hover:bg-white/10 active:bg-white/20 rounded-full transition disabled:opacity-30 text-white"
          >
            <ZoomIn className="w-5 h-5" />
          </button>

          <div className="h-6 w-[1px] bg-white/20 mx-1" />

          <button 
            onClick={onClose} 
            className="p-2 bg-white/10 hover:bg-white/20 active:scale-95 rounded-full text-white transition duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div 
        className="flex-1 w-full flex items-center justify-center overflow-hidden relative cursor-grab active:cursor-grabbing"
        onMouseDown={e => handleStart(e.clientX, e.clientY)}
        onMouseMove={e => handleMove(e.clientX, e.clientY)}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={e => handleStart(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchMove={e => handleMove(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchEnd={handleEnd}
        onDoubleClick={handleDoubleTap}
      >
        <img
          ref={imgRef}
          src={file.file_path}
          alt={file.file_name}
          className="max-w-[95vw] max-h-[75vh] object-contain transition-transform duration-75 select-none pointer-events-none"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          }}
        />
      </div>

      <div className="p-4 bg-gradient-to-t from-black/80 to-transparent text-center text-xs text-gray-400">
        💡 اسحب لتحريك الصورة عند التكبير • اضغط مرتين للتكبير التلقائي
      </div>
    </div>
  );
}

/**
 * Individual Submission Attachment Card Component
 */
function AttachmentCard({ file, getDownloadUrl }) {
  const [copied, setCopied] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  const isDeleted = !file.file_path;
  const fileUrl = isDeleted ? null : (file.file_path || getDownloadUrl(file.id));
  const isVideo = !isDeleted && /\.(mp4|webm|mov|ogg|mkv)$/i.test(file.file_name || fileUrl);
  const isImage = !isDeleted && /\.(jpg|jpeg|png|gif|webp)$/i.test(file.file_name || fileUrl);
  const isZip = !isDeleted && /\.(zip|rar|tar|gz)$/i.test(file.file_name || fileUrl);
  const isPdf = !isDeleted && /\.pdf$/i.test(file.file_name || fileUrl);

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    if (isDeleted) return;
    try {
      const fullUrl = window.location.origin + fileUrl;
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('تم نسخ رابط الملف بنجاح!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('فشل في نسخ الرابط');
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (isDeleted) return;
    const fullUrl = window.location.origin + fileUrl;
    if (navigator.share) {
      try {
        await navigator.share({
          title: file.file_name,
          text: `شاهد ملف التسليم: ${file.file_name}`,
          url: fullUrl,
        });
        toast.success('تمت المشاركة بنجاح');
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('حدث خطأ أثناء المشاركة');
        }
      }
    } else {
      handleCopyLink(e);
    }
  };

  const renderIcon = () => {
    if (isDeleted) return <FileText className="w-8 h-8 text-gray-300 dark:text-gray-600" />;
    if (isVideo) return <Video className="w-8 h-8 text-indigo-500" />;
    if (isImage) return <Image className="w-8 h-8 text-emerald-500" />;
    if (isZip) return <Archive className="w-8 h-8 text-amber-500" />;
    if (isPdf) return <FileText className="w-8 h-8 text-red-500" />;
    return <File className="w-8 h-8 text-blue-500" />;
  };

  if (isDeleted) {
    return (
      <div className="flex flex-col bg-gray-50/50 dark:bg-black/10 border border-dashed border-gray-200 dark:border-gray-800/80 rounded-2xl overflow-hidden p-4 text-center justify-between min-h-[170px] opacity-75">
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800/50 flex items-center justify-center mb-1">
            {renderIcon()}
          </div>
          <h4 className="font-bold text-xs text-gray-400 dark:text-gray-500 line-through line-clamp-1 truncate w-full" title={file.file_name}>
            {file.file_name}
          </h4>
          <p className="text-[10px] text-gray-400 dark:text-gray-500">
            الحجم: {formatFileSize(file.file_size)}
          </p>
        </div>
        <div className="bg-red-50/50 dark:bg-red-950/20 text-red-500 dark:text-red-400 p-2.5 rounded-xl text-[10px] font-bold border border-red-100/30 dark:border-red-900/10 mt-3 leading-relaxed">
          ⚠️ تم حذف الملف الفعلي لتوفير المساحة بعد مرور 30 يوماً من تسليم المهمة.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="group relative flex flex-col bg-white dark:bg-[#1a1b1e] border border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-primary/20 dark:hover:border-primary/30 transition-all duration-300">
        <div className="relative aspect-video w-full bg-gray-50 dark:bg-black/20 flex items-center justify-center overflow-hidden border-b border-gray-100 dark:border-gray-800">
          {isVideo && (
            <video 
              src={fileUrl} 
              controls 
              playsInline 
              preload="metadata" 
              className="w-full h-full object-contain"
            />
          )}

          {isImage && (
            <div className="relative w-full h-full cursor-zoom-in" onClick={() => setShowLightbox(true)}>
              <img 
                src={fileUrl} 
                alt={file.file_name} 
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
              />
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                <span className="p-2 bg-black/60 backdrop-blur-sm rounded-full text-white">
                  <Eye className="w-5 h-5" />
                </span>
              </div>
            </div>
          )}

          {!isVideo && !isImage && (
            <div className="flex flex-col items-center gap-2 p-4 text-center">
              {renderIcon()}
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-100 dark:bg-gray-800/80 px-2.5 py-0.5 rounded-full mt-1">
                {file.file_name?.split('.').pop() || 'DOCUMENT'}
              </span>
            </div>
          )}
        </div>

        <div className="p-3.5 space-y-3">
          <div className="text-right">
            <h4 className="font-bold text-xs text-gray-800 dark:text-gray-200 line-clamp-1 truncate" title={file.file_name}>
              {file.file_name}
            </h4>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
              حجم الملف: {formatFileSize(file.file_size)}
            </p>
          </div>

          <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100 dark:border-gray-800/80">
            <a 
              href={fileUrl} 
              download={file.file_name}
              className="flex-1 py-1.5 px-2 bg-primary/5 hover:bg-primary text-primary hover:text-white rounded-xl text-[11px] font-bold transition duration-200 flex items-center justify-center gap-1"
            >
              <Download className="w-3.5 h-3.5" />
              <span>تحميل</span>
            </a>

            <button 
              onClick={handleCopyLink}
              className="p-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700/80 text-gray-600 dark:text-gray-400 rounded-xl transition duration-200 relative group"
              title="نسخ الرابط"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              {copied && (
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[9px] px-2 py-0.5 rounded shadow-sm whitespace-nowrap animate-bounce">
                  تم النسخ!
                </span>
              )}
            </button>

            <button 
              onClick={handleShare}
              className="p-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700/80 text-gray-600 dark:text-gray-400 rounded-xl transition duration-200"
              title="مشاركة الملف"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {showLightbox && (
        <FileImageLightbox file={file} onClose={() => setShowLightbox(false)} />
      )}
    </>
  );
}

/**
 * Grid Component Container for Submissions attachments
 */
function SubmissionFilePreview({ files, getDownloadUrl }) {
  if (!files || files.length === 0) return null;

  return (
    <div className="space-y-2.5">
      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
        📎 المرفقات ({files.length})
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {files.map(file => (
          <AttachmentCard key={file.id} file={file} getDownloadUrl={getDownloadUrl} />
        ))}
      </div>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const info = TM_STATUS_MAP[status] || { label: status };
  const cls = TM_STATUS_COLORS[status] || TM_STATUS_COLORS.DRAFT;
  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${cls}`}>{info.label}</span>;
};

export default function TmTasksPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwner = ['owner', 'admin'].includes(user.role);
  const isSupervisor = user.role === 'supervisor' || isOwner;
  const isTeamLeader = user.role === 'team_leader';
  const isTeamMember = user.role === 'team_member';

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [showAssign, setShowAssign] = useState(null);
  const [showDistribute, setShowDistribute] = useState(null);
  const [showSubmit, setShowSubmit] = useState(null);
  const [viewMode, setViewMode] = useState('kanban'); // kanban | list
  const [mobileTab, setMobileTab] = useState('todo');
  const [doneTab, setDoneTab] = useState('accepted'); // 'accepted' | 'rejected'
  const [filterType, setFilterType] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterSeconds, setFilterSeconds] = useState(''); // '' | 'completed' | 'uncompleted'
  const [filterDateType, setFilterDateType] = useState('deadline'); // 'deadline' | 'accepted_at' | 'created_at'
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [clients, setClients] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [ratePerSecond, setRatePerSecond] = useState(500);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTmTasks({});
      setTasks(data);
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ في تحميل المهام'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getTmClients().then(d => setClients(Array.isArray(d) ? d : d.clients || [])).catch(() => {});
    getTmTeams().then(setTeams).catch(() => {});
    getTmRatePerSecond().then(setRatePerSecond).catch(() => {});
  }, []);

  const applyDatePreset = (preset) => {
    const today = new Date();
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    const todayStr = formatDate(today);
    
    switch (preset) {
      case 'today':
        setFilterStartDate(todayStr);
        setFilterEndDate(todayStr);
        break;
      case 'yesterday': {
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        setFilterStartDate(formatDate(yesterday));
        setFilterEndDate(formatDate(yesterday));
        break;
      }
      case 'last7': {
        const prev7 = new Date();
        prev7.setDate(today.getDate() - 7);
        setFilterStartDate(formatDate(prev7));
        setFilterEndDate(todayStr);
        break;
      }
      case 'thisMonth': {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        setFilterStartDate(formatDate(firstDay));
        setFilterEndDate(formatDate(lastDay));
        break;
      }
      case 'lastMonth': {
        const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        setFilterStartDate(formatDate(firstDayLastMonth));
        setFilterEndDate(formatDate(lastDayLastMonth));
        break;
      }
      case 'clear':
      default:
        setFilterStartDate('');
        setFilterEndDate('');
        break;
    }
  };

  const activeFiltersCount = [
    filterType, filterTeam, filterClient, filterSeconds, filterStartDate, filterEndDate
  ].filter(Boolean).length;

  const filtered = tasks.filter(t => {
    // Search filter
    if (search) {
      const cleanSearch = search.toLowerCase().trim();
      const taskCodeStr = `ms-${String(t.id).padStart(4, '0')}`;
      const hasMatch = 
        t.title?.toLowerCase().includes(cleanSearch) ||
        t.client_name?.toLowerCase().includes(cleanSearch) ||
        t.display_client_name?.toLowerCase().includes(cleanSearch) ||
        taskCodeStr.includes(cleanSearch) ||
        String(t.id).includes(cleanSearch);
        
      if (!hasMatch) return false;
    }
    
    // Type filter
    if (filterType && t.video_type !== filterType) return false;
    
    // Team filter
    if (filterTeam && !(t.assigned_teams || []).some(tt => String(tt.team_id) === filterTeam)) return false;
    
    // Client filter
    if (filterClient && String(t.client_id) !== filterClient) return false;
    
    // Seconds status filter
    if (filterSeconds === 'completed') {
      if (t.status !== 'ACCEPTED') return false;
    } else if (filterSeconds === 'uncompleted') {
      if (['ACCEPTED', 'CANCELLED', 'REJECTED', 'ARCHIVED'].includes(t.status)) return false;
    }
    
    // Date range filter
    const targetDateStr = 
      filterDateType === 'accepted_at' ? t.accepted_at?.split('T')[0] :
      filterDateType === 'created_at' ? t.created_at?.split('T')[0] :
      t.deadline?.split('T')[0];
      
    if (targetDateStr) {
      if (filterStartDate && targetDateStr < filterStartDate) return false;
      if (filterEndDate && targetDateStr > filterEndDate) return false;
    } else if (filterStartDate || filterEndDate) {
      // If task lacks the target date and filter is active, exclude it
      return false;
    }
    
    return true;
  });

  // Group tasks by Kanban column
  const columnTasks = {};
  KANBAN_COLUMNS.forEach(col => {
    let list = filtered.filter(t => col.statuses.includes(t.status));
    if (col.key === 'done') {
      if (doneTab === 'accepted') {
        list = list.filter(t => ['ACCEPTED', 'LOST'].includes(t.status));
      } else {
        list = list.filter(t => ['REJECTED', 'CANCELLED'].includes(t.status));
      }
      list.sort((a, b) => {
        const valA = a.accepted_at ? new Date(a.accepted_at).getTime() : 0;
        const valB = b.accepted_at ? new Date(b.accepted_at).getTime() : 0;
        if (valA !== valB) return valB - valA; // newest acceptance first
        // Fallback to task ID
        return b.id - a.id;
      });
    }
    columnTasks[col.key] = list;
  });

  const todayStr = new Date().toLocaleDateString('en-CA'); // 'YYYY-MM-DD'
  const activeTasks = filtered.filter(t => ['ASSIGNED', 'IN_PROGRESS', 'REVISION', 'SUBMITTED', 'TL_APPROVED'].includes(t.status));
  const activeTasksCount = activeTasks.length;
  const unfinishedSeconds = activeTasks.reduce((sum, t) => sum + (t.video_seconds || 0), 0);
  const requiredSecondsToday = activeTasks
    .filter(t => t.deadline && t.deadline <= todayStr)
    .reduce((sum, t) => sum + (t.video_seconds || 0), 0);

  // Advanced filters seconds totals
  const filteredCompletedSeconds = filtered
    .filter(t => t.status === 'ACCEPTED')
    .reduce((sum, t) => sum + (t.video_seconds || 0), 0);

  const filteredUncompletedSeconds = filtered
    .filter(t => !['ACCEPTED', 'CANCELLED', 'REJECTED', 'ARCHIVED'].includes(t.status))
    .reduce((sum, t) => sum + (t.video_seconds || 0), 0);

  return (
    <div className="p-4 md:p-6 space-y-5" dir="rtl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-blue-500/20 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-dark-h)]">لوحة المهام</h1>
            <p className="text-sm text-[var(--text-muted)]">{tasks.length} مهمة — {tasks.filter(t => t.status === 'ACCEPTED').length} مقبولة</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="bg-[var(--bg-flat-50)] p-1 rounded-xl flex items-center border border-[var(--border-light)]">
            <button onClick={() => setViewMode('kanban')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'kanban' ? 'bg-white dark:bg-[#1a1b1e] text-primary shadow-sm' : 'text-[var(--text-muted)]'}`}><ClipboardList className="w-4 h-4" /></button>
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-[#1a1b1e] text-primary shadow-sm' : 'text-[var(--text-muted)]'}`}><FileText className="w-4 h-4" /></button>
          </div>

          {isSupervisor && (
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-xl transition-all font-bold text-sm">
              <Plus className="w-4 h-4" /> مهمة جديدة
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Active Tasks Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition duration-200">
          <div className="w-12 h-12 rounded-2xl bg-blue-500/10 text-blue-500 flex items-center justify-center shrink-0">
            <ClipboardList className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-[var(--text-gray)]">عدد المهام النشطة</p>
            <p className="text-xl font-black text-[var(--text-dark-h)] mt-0.5">{activeTasksCount}</p>
          </div>
        </div>

        {/* Required Seconds for Today Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition duration-200">
          <div className="w-12 h-12 rounded-2xl bg-yellow-500/10 text-yellow-500 flex items-center justify-center shrink-0">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-[var(--text-gray)]">الثواني المطلوبة لليوم</p>
            <p className="text-xl font-black text-[var(--text-dark-h)] mt-0.5">{formatSeconds(requiredSecondsToday)}</p>
          </div>
        </div>

        {/* Unfinished Seconds Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition duration-200">
          <div className="w-12 h-12 rounded-2xl bg-purple-500/10 text-purple-500 flex items-center justify-center shrink-0">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-[var(--text-gray)]">الثواني غير المنجزة</p>
            <p className="text-xl font-black text-[var(--text-dark-h)] mt-0.5">{formatSeconds(unfinishedSeconds)}</p>
          </div>
        </div>
      </div>

      {/* Search & Toggle Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-[var(--text-muted)]" />
          <input 
            type="text" 
            placeholder="بحث في لوحة المهام بالعنوان أو العميل..." 
            value={search} 
            onChange={e => setSearch(e.target.value)}
            className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--card-bg)] text-[var(--text-dark-h)] text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition duration-200" 
          />
        </div>
        {/* Toggle Filters Button */}
        <button 
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-bold transition-all duration-200 shrink-0 ${
            showFilters || activeFiltersCount > 0
              ? 'bg-primary text-white border-primary shadow-md shadow-primary/10 hover:bg-primary-dark'
              : 'bg-[var(--card-bg)] text-[var(--text-dark-h)] border-[var(--border-light)] hover:bg-[var(--bg-flat-50)]'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span>التصفية المتقدمة</span>
          {activeFiltersCount > 0 && (
            <span className="bg-white text-primary text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Collapsible Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-5 space-y-5 shadow-sm transition-all duration-300 animate-slideDown">
          {/* Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            
            {/* Column 1: Client Selector & Team & Type (Span 4) */}
            <div className="md:col-span-4 space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">العميل / المشترك</label>
                <select 
                  value={filterClient} 
                  onChange={e => setFilterClient(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition duration-150"
                >
                  <option value="">كل المشتركين/العملاء</option>
                  {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">النوع</label>
                  <select 
                    value={filterType} 
                    onChange={e => setFilterType(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition duration-150"
                  >
                    <option value="">الكل</option>
                    <option value="ugc">UGC</option>
                    <option value="motion">Motion</option>
                    <option value="podcast">Podcast</option>
                    <option value="reel">Reel</option>
                    <option value="tvc">TVC</option>
                    <option value="cartoon">Cartoon</option>
                    <option value="other">أخرى</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">الفريق</label>
                  <select 
                    value={filterTeam} 
                    onChange={e => setFilterTeam(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition duration-150"
                  >
                    <option value="">الكل</option>
                    {teams.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Column 2: Date Type & Preset Shortcuts (Span 4) */}
            <div className="md:col-span-4 space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">نوع التاريخ للبحث</label>
                <div className="flex bg-[var(--bg-flat-50)] p-1 rounded-xl border border-[var(--border-light)]">
                  <button 
                    type="button"
                    onClick={() => setFilterDateType('deadline')}
                    className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold transition duration-150 ${
                      filterDateType === 'deadline' 
                        ? 'bg-white dark:bg-[#1a1b1e] text-primary shadow-sm' 
                        : 'text-[var(--text-muted)] hover:text-[var(--text-dark-h)]'
                    }`}
                  >
                    الموعد (Deadline)
                  </button>
                  <button 
                    type="button"
                    onClick={() => setFilterDateType('accepted_at')}
                    className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold transition duration-150 ${
                      filterDateType === 'accepted_at' 
                        ? 'bg-white dark:bg-[#1a1b1e] text-primary shadow-sm' 
                        : 'text-[var(--text-muted)] hover:text-[var(--text-dark-h)]'
                    }`}
                  >
                    القبول (Acceptance)
                  </button>
                  <button 
                    type="button"
                    onClick={() => setFilterDateType('created_at')}
                    className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold transition duration-150 ${
                      filterDateType === 'created_at' 
                        ? 'bg-white dark:bg-[#1a1b1e] text-primary shadow-sm' 
                        : 'text-[var(--text-muted)] hover:text-[var(--text-dark-h)]'
                    }`}
                  >
                    الإنشاء (Creation)
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">اختصارات الفترة الزمنية</label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { key: 'today', label: 'اليوم' },
                    { key: 'yesterday', label: 'أمس' },
                    { key: 'last7', label: 'آخر 7 أيام' },
                    { key: 'thisMonth', label: 'هذا الشهر' },
                    { key: 'lastMonth', label: 'الشهر الماضي' },
                  ].map(preset => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => applyDatePreset(preset.key)}
                      className="px-2.5 py-1.5 bg-[var(--bg-flat-50)] hover:bg-[var(--border-light)] text-[var(--text-muted)] hover:text-[var(--text-dark-h)] rounded-lg text-[11px] font-semibold border border-[var(--border-light)] transition duration-150"
                    >
                      {preset.label}
                    </button>
                  ))}
                  {(filterStartDate || filterEndDate) && (
                    <button
                      type="button"
                      onClick={() => applyDatePreset('clear')}
                      className="px-2.5 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-lg text-[11px] font-bold border border-red-500/20 transition duration-150"
                    >
                      ✕ مسح التاريخ
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Column 3: Seconds Completed Status & Custom Date Range inputs (Span 4) */}
            <div className="md:col-span-4 space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">حالة ثواني المهمة</label>
                <div className="flex bg-[var(--bg-flat-50)] p-1 rounded-xl border border-[var(--border-light)]">
                  <button 
                    type="button"
                    onClick={() => setFilterSeconds('')}
                    className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold transition duration-150 ${
                      filterSeconds === '' 
                        ? 'bg-white dark:bg-[#1a1b1e] text-primary shadow-sm' 
                        : 'text-[var(--text-muted)] hover:text-[var(--text-dark-h)]'
                    }`}
                  >
                    الكل
                  </button>
                  <button 
                    type="button"
                    onClick={() => setFilterSeconds('completed')}
                    className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold transition duration-150 ${
                      filterSeconds === 'completed' 
                        ? 'bg-white dark:bg-[#1a1b1e] text-emerald-600 dark:text-emerald-400 shadow-sm' 
                        : 'text-[var(--text-muted)] hover:text-[var(--text-dark-h)]'
                    }`}
                  >
                    المنجزة (مقبولة ✅)
                  </button>
                  <button 
                    type="button"
                    onClick={() => setFilterSeconds('uncompleted')}
                    className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold transition duration-150 ${
                      filterSeconds === 'uncompleted' 
                        ? 'bg-white dark:bg-[#1a1b1e] text-amber-600 dark:text-amber-400 shadow-sm' 
                        : 'text-[var(--text-muted)] hover:text-[var(--text-dark-h)]'
                    }`}
                  >
                    غير المنجزة (نشطة 🔄)
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[var(--text-gray)] mb-1.5">الفترة الزمنية المخصصة</label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-[var(--text-muted)] font-black">من:</span>
                    <input 
                      type="date" 
                      value={filterStartDate} 
                      onChange={e => setFilterStartDate(e.target.value)}
                      className="w-full pr-8 pl-2 py-2 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-xs focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition duration-150" 
                    />
                  </div>
                  <div className="relative flex-1">
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-[var(--text-muted)] font-black">إلى:</span>
                    <input 
                      type="date" 
                      value={filterEndDate} 
                      onChange={e => setFilterEndDate(e.target.value)}
                      className="w-full pr-8 pl-2 py-2 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-xs focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition duration-150" 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Results Summary Line */}
          <div className="bg-[var(--bg-flat-50)] rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4 border border-[var(--border-light)]">
            <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-[var(--text-gray)]">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[var(--card-bg)] border border-[var(--border-light)] text-[var(--text-dark-h)]">
                <span>🎯 المهام المطابقة:</span>
                <strong className="text-primary font-black text-sm">{filtered.length}</strong>
              </div>
              
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-500/5 border border-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <span>✅ ثوانٍ منجزة:</span>
                <strong className="font-black text-sm">{formatSeconds(filteredCompletedSeconds)}</strong>
              </div>

              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/5 border border-amber-500/10 text-amber-600 dark:text-amber-400">
                <span>🔄 ثوانٍ غير منجزة:</span>
                <strong className="font-black text-sm">{formatSeconds(filteredUncompletedSeconds)}</strong>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-xs font-black text-[var(--text-dark-h)] px-4 py-1.5 bg-primary/10 text-primary rounded-xl border border-primary/20">
              <span>⏱️ إجمالي الثواني المفلترة:</span>
              <span className="text-sm font-black">{formatSeconds(filteredCompletedSeconds + filteredUncompletedSeconds)}</span>
            </div>
          </div>

          {/* Reset All Filters button when active */}
          {activeFiltersCount > 0 && (
            <div className="flex justify-end pt-1">
              <button 
                onClick={() => {
                  setSearch('');
                  setFilterType('');
                  setFilterTeam('');
                  setFilterClient('');
                  setFilterSeconds('');
                  setFilterStartDate('');
                  setFilterEndDate('');
                }}
                className="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold shadow-md shadow-red-500/10 transition duration-150"
              >
                ✕ مسح كافة التصفية والبحث
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {viewMode === 'list' ? (
            <div className="overflow-x-auto bg-[var(--card-bg)] rounded-xl border border-[var(--card-border)]">
              <table className="w-full text-sm text-right border-collapse">
                <thead>
                  <tr className="bg-[var(--bg-flat-50)] text-[var(--text-gray)] border-b border-[var(--border-light)]">
                    <th className="p-3">المهمة</th>
                    <th className="p-3">العميل</th>
                    <th className="p-3">الفرق</th>
                    <th className="p-3">المدة</th>
                    <th className="p-3">الحالة</th>
                    <th className="p-3">تاريخ التسليم</th>
                    <th className="p-3">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan="7" className="p-4 text-center text-muted">لا توجد مهام</td></tr>
                  ) : filtered.map(t => (
                    <tr key={t.id} className="border-b border-[var(--border-light)] hover:bg-[var(--bg-flat-50)]">
                       <td className="p-3 font-bold text-[var(--text-dark-h)] flex items-center gap-1.5">
                         <span className="text-primary font-mono text-xs bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20 shrink-0">
                           {formatTaskCode(t.id)}
                         </span>
                         <span className="truncate">{t.title}</span>
                         {t.team_count > 1 && <span className="mr-2 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">🏆 {t.team_count} فرق</span>}
                       </td>
                       <td className="p-3">{t.display_client_name || t.client_name || t.guest_client_name || '—'}</td>
                       <td className="p-3">
                         {t.assigned_teams?.length > 0 ? t.assigned_teams.map(tt => tt.team_name).join(' · ') : <span className="text-gray-400">بدون فريق</span>}
                       </td>
                       <td className="p-3 font-bold text-[var(--text-dark-h)]">{t.video_seconds ? formatSeconds(t.video_seconds) : '—'}</td>
                       <td className="p-3"><StatusBadge status={t.status} /></td>
                       <td className="p-3"><span className={t.deadline && isPast(parseISO(t.deadline)) && !isToday(parseISO(t.deadline)) && !['ACCEPTED', 'LOST', 'CANCELLED', 'ARCHIVED', 'REJECTED'].includes(t.status) ? 'text-red-500 font-bold' : ''}>{t.deadline || '—'}</span></td>
                       <td className="p-3">
                         <button onClick={() => loadTaskDetail(t.id)} className="text-primary bg-primary/10 p-1.5 rounded-lg hover:bg-primary/20 transition"><Eye className="w-4 h-4"/></button>
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              {/* ── Mobile: Tab Selector ── */}
              <div className="flex xl:hidden gap-2 overflow-x-auto pb-2">
                {KANBAN_COLUMNS.map(col => {
                  const colTasks = columnTasks[col.key] || [];
                  const colSeconds = colTasks.reduce((sum, t) => sum + (t.video_seconds || 0), 0);
                  return (
                    <button key={col.key} onClick={() => setMobileTab(col.key)}
                      className={`px-3 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                        mobileTab === col.key 
                          ? 'bg-primary text-white shadow-md shadow-primary/15' 
                          : 'bg-[var(--card-bg)] text-[var(--text-gray)] border border-[var(--card-border)] hover:bg-[var(--bg-flat-50)]'
                      }`}>
                      <span>{col.label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        mobileTab === col.key ? 'bg-white/20 text-white' : 'bg-[var(--bg-flat-50)] text-primary'
                      }`}>
                        {colTasks.length} ({formatSeconds(colSeconds)})
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* ── Desktop: 4-Column Kanban ── */}
              <div className={`hidden xl:grid xl:grid-cols-4 gap-4`}>
                {KANBAN_COLUMNS.map(col => (
                  <KanbanColumn key={col.key} column={col} tasks={columnTasks[col.key]} user={user} ratePerSecond={ratePerSecond}
                    doneTab={doneTab} setDoneTab={setDoneTab}
                    onView={loadTaskDetail} onAssign={t => setShowAssign(t)} onEdit={t => setEditTask(t)} onDistribute={t => setShowDistribute(t)} onSubmit={t => setShowSubmit(t)} />
                ))}
              </div>

              {/* ── Mobile: Single Column ── */}
              <div className="xl:hidden space-y-3">
                {mobileTab === 'done' && (
                  <div className="flex bg-[var(--bg-flat-50)] p-0.5 rounded-xl border border-[var(--border-light)] text-xs mb-3">
                    <button
                      onClick={() => setDoneTab('accepted')}
                      className={`flex-1 py-2 rounded-lg font-bold transition flex items-center justify-center gap-1.5 ${
                        doneTab === 'accepted'
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'
                      }`}
                    >
                      المقبولة ✅
                    </button>
                    <button
                      onClick={() => setDoneTab('rejected')}
                      className={`flex-1 py-2 rounded-lg font-bold transition flex items-center justify-center gap-1.5 ${
                        doneTab === 'rejected'
                          ? 'bg-red-500 text-white shadow-sm'
                          : 'text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'
                      }`}
                    >
                      المرفوضة ❌
                    </button>
                  </div>
                )}
                {(columnTasks[mobileTab] || []).length === 0 ? (
                  <div className="text-center py-8 text-[var(--text-muted)] text-sm">لا توجد مهام في هذا القسم</div>
                ) : mobileTab === 'progress' ? (
                  Object.entries(
                    (columnTasks[mobileTab] || []).reduce((acc, task) => {
                      if (!task.assigned_teams || task.assigned_teams.length === 0) {
                        if (!acc['بدون فريق']) acc['بدون فريق'] = [];
                        acc['بدون فريق'].push(task);
                      } else {
                        task.assigned_teams.forEach(t => {
                          const tName = t.team_name;
                          if (!acc[tName]) acc[tName] = [];
                          if (!acc[tName].find(x => x.id === task.id)) {
                            acc[tName].push(task);
                          }
                        });
                      }
                      return acc;
                    }, {})
                  ).map(([teamName, teamTasks]) => (
                    <div key={teamName} className="space-y-2 pb-2">
                      <div className="text-sm font-bold text-[var(--text-gray)] mt-2 mb-1 px-1 flex items-center gap-1.5 border-b border-[var(--border-light)] pb-1">
                        🏢 {teamName} <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full">{teamTasks.length}</span>
                      </div>
                      {teamTasks.map(task => (
                        <TaskCard key={task.id} task={task} user={user} ratePerSecond={ratePerSecond}
                          onView={() => loadTaskDetail(task.id)} onAssign={() => setShowAssign(task)}
                          onEdit={() => setEditTask(task)}
                          onDistribute={() => setShowDistribute(task)} onSubmit={() => setShowSubmit(task)} />
                      ))}
                    </div>
                  ))
                ) : (
                  (columnTasks[mobileTab] || []).map(task => (
                    <TaskCard key={task.id} task={task} user={user} ratePerSecond={ratePerSecond}
                      onView={() => loadTaskDetail(task.id)} onAssign={() => setShowAssign(task)}
                      onEdit={() => setEditTask(task)}
                      onDistribute={() => setShowDistribute(task)} onSubmit={() => setShowSubmit(task)} />
                  ))
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Modals ── */}
      {showCreate && <TaskFormModal user={user}
        onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
        
      {editTask && <TaskFormModal user={user} task={editTask}
        onClose={() => setEditTask(null)} onCreated={() => { setEditTask(null); load(); }} />}

      {showDetail && <TaskDetailModal task={showDetail} user={user} teams={teams} ratePerSecond={ratePerSecond}
        onClose={() => setShowDetail(null)} onRefresh={() => loadTaskDetail(showDetail.id)} onReload={load} />}

      {showAssign && <AssignModal task={showAssign} teams={teams}
        onClose={() => setShowAssign(null)} onDone={() => { setShowAssign(null); load(); }} />}

      {showDistribute && <DistributeModal 
        task={showDistribute.task || showDistribute} 
        initialTeamId={showDistribute.teamId || null}
        user={user}
        onClose={() => setShowDistribute(null)} 
        onDone={() => { 
          setShowDistribute(null); 
          load(); 
          if (showDetail) {
            loadTaskDetail(showDetail.id);
          }
        }} 
      />}

      {showSubmit && <SubmitModal task={showSubmit} user={user}
        onClose={() => setShowSubmit(null)} onDone={() => { setShowSubmit(null); load(); }} />}
    </div>
  );

  async function loadTaskDetail(id) {
    try {
      const detail = await getTmTask(id);
      setShowDetail(detail);
    } catch (e) { toast.error('خطأ في تحميل التفاصيل'); }
  }
}

// ─── Kanban Column ────────────────────────────────────────────────────────────
function KanbanColumn({ column, tasks, user, ratePerSecond = 500, doneTab, setDoneTab, onView, onAssign, onEdit, onDistribute, onSubmit }) {
  const colorMap = {
    gray: 'bg-gray-500', yellow: 'bg-yellow-500', purple: 'bg-purple-500', green: 'bg-green-500', red: 'bg-red-500'
  };
  return (
    <div className="bg-[var(--card-bg)]/50 rounded-2xl border border-[var(--card-border)] flex flex-col min-h-[300px]">
      {/* Column Header */}
      <div className="p-3 border-b border-[var(--border-light)]">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-bold text-sm text-[var(--text-dark-h)] whitespace-nowrap shrink-0">{column.label}</h3>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-xs bg-[var(--bg-flat-50)] text-[var(--text-gray)] px-2 py-1 rounded-full font-bold">{tasks.length}</span>
            <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-md font-black">{formatSeconds(tasks.reduce((sum, t) => sum + (t.video_seconds || 0), 0))}</span>
          </div>
        </div>
        <div className={`h-1 mt-2 rounded-full ${
          column.key === 'done' 
            ? (doneTab === 'accepted' ? 'bg-green-500' : 'bg-red-500') 
            : colorMap[column.color]
        } opacity-60`} />

        {column.key === 'done' && (
          <div className="mt-2.5 flex bg-[var(--bg-flat-50)] p-0.5 rounded-lg border border-[var(--border-light)] text-[10px]">
            <button
              onClick={() => setDoneTab('accepted')}
              className={`flex-1 py-1 rounded-md font-bold transition ${
                doneTab === 'accepted'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'
              }`}
            >
              المقبولة ✅
            </button>
            <button
              onClick={() => setDoneTab('rejected')}
              className={`flex-1 py-1 rounded-md font-bold transition ${
                doneTab === 'rejected'
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'text-[var(--text-gray)] hover:bg-[var(--bg-flat-100)]'
              }`}
            >
              المرفوضة ❌
            </button>
          </div>
        )}
      </div>

      {/* Cards */}
      <div className="flex-1 p-2 space-y-2.5 overflow-y-auto max-h-[65vh] custom-scrollbar">
        {tasks.length === 0 ? (
          <div className="text-center py-6 text-[var(--text-muted)] text-xs">فارغ</div>
        ) : column.key === 'progress' ? (
          Object.entries(
            tasks.reduce((acc, task) => {
              if (!task.assigned_teams || task.assigned_teams.length === 0) {
                if (!acc['بدون فريق']) acc['بدون فريق'] = [];
                acc['بدون فريق'].push(task);
              } else {
                task.assigned_teams.forEach(t => {
                  const tName = t.team_name;
                  if (!acc[tName]) acc[tName] = [];
                  // Prevent duplicating the same task in the same team bucket
                  if (!acc[tName].find(x => x.id === task.id)) {
                    acc[tName].push(task);
                  }
                });
              }
              return acc;
            }, {})
          ).map(([teamName, teamTasks]) => (
            <div key={teamName} className="space-y-2 pb-2">
              <div className="text-xs font-bold text-[var(--text-gray)] mt-2 mb-1 px-1 flex items-center gap-1.5">
                <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full">{teamTasks.length}</span>
                🏢 {teamName}
              </div>
              {teamTasks.map(task => (
                <TaskCard key={task.id} task={task} user={user} compact ratePerSecond={ratePerSecond}
                  onView={() => onView(task.id)} onAssign={() => onAssign(task)}
                  onEdit={() => onEdit(task)}
                  onDistribute={() => onDistribute(task)} onSubmit={() => onSubmit(task)} />
              ))}
            </div>
          ))
        ) : (
          tasks.map(task => (
            <TaskCard key={task.id} task={task} user={user} compact ratePerSecond={ratePerSecond}
              onView={() => onView(task.id)} onAssign={() => onAssign(task)}
              onEdit={() => onEdit(task)}
              onDistribute={() => onDistribute(task)} onSubmit={() => onSubmit(task)} />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Task Card ────────────────────────────────────────────────────────────────
function TaskCard({ task, user, compact, ratePerSecond = 500, onView, onAssign, onEdit, onDistribute, onSubmit }) {
  const isOwner = ['owner', 'admin'].includes(user.role);
  const isSupervisor = user.role === 'supervisor' || isOwner;
  const isTeamLeader = user.role === 'team_leader';
  const isTeamMember = user.role === 'team_member';
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const multiplier = MULTIPLIER_CONFIG[task.multiplier] || MULTIPLIER_CONFIG[1];
  const price = !isTeamMember ? (task.video_seconds || 0) * (task.multiplier || 1) * ratePerSecond : null;
  const clientName = task.display_client_name || task.client_name || task.guest_client_name;

  let deadlineCls = 'text-[var(--text-gray)]';
  let deadlineIconCls = 'text-[var(--text-gray)]';
  if (task.deadline && !['ACCEPTED', 'LOST', 'ARCHIVED', 'CANCELLED', 'REJECTED'].includes(task.status)) {
    const dDate = parseISO(task.deadline);
    if (isPast(dDate) && !isToday(dDate)) {
      deadlineCls = 'text-red-500 font-bold'; deadlineIconCls = 'text-red-500';
    } else if (isToday(dDate)) {
      deadlineCls = 'text-orange-500 font-bold'; deadlineIconCls = 'text-orange-500';
    }
  }

  return (
    <div className={`bg-[var(--card-bg)] border border-[var(--card-border)] ${compact ? 'rounded-xl p-3' : 'rounded-2xl p-4'} hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer group`}
      onClick={onView}>
      {/* Top: Priority + Status */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${priority.cls}`}>{priority.label}</span>
        <StatusBadge status={task.status} />
      </div>

      {/* Title */}
      <h3 className={`font-bold text-[var(--text-dark-h)] group-hover:text-primary transition-colors line-clamp-2 mb-2 ${compact ? 'text-xs' : 'text-sm'}`}>
        <span className="text-primary font-black ml-1.5 font-mono text-[10px] bg-primary/10 px-1.5 py-0.5 rounded border border-primary/20 shrink-0">
          {formatTaskCode(task.id)}
        </span>
        {task.title}
      </h3>

      {/* Info Row */}
      <div className={`space-y-1.5 ${compact ? 'text-[10px]' : 'text-xs'} text-[var(--text-gray)]`}>
        {/* Client */}
        {clientName && (
          <div className="flex items-center gap-1.5">
            <span className="text-green-500">🟢</span>
            <span className="truncate">{clientName}</span>
            {!task.client_id && task.guest_client_name && (
              <span className="text-[9px] bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 px-1 py-0.5 rounded-full">🚶</span>
            )}
          </div>
        )}

        {/* Team + Assignee */}
        {task.assigned_teams?.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Users className="w-3 h-3 text-[var(--text-muted)] animate-pulse" />
            {isSupervisor ? (
              <div className="flex flex-wrap gap-1">
                {task.assigned_teams.map((t, idx) => {
                  let statusLabel = 'للتنفيذ';
                  let statusCls = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                  if (t.status === 'in_progress') {
                    statusLabel = 'قيد العمل ⚡';
                    statusCls = 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400';
                  } else if (t.status === 'submitted') {
                    statusLabel = 'قيد المراجعة 🔍';
                    statusCls = 'bg-purple-500/10 text-purple-600 dark:text-purple-400';
                  } else if (t.status === 'won') {
                    statusLabel = 'فائز 🏆';
                    statusCls = 'bg-green-500/10 text-green-600 dark:text-green-400';
                  } else if (t.status === 'lost') {
                    statusLabel = 'خسر ❌';
                    statusCls = 'bg-red-500/10 text-red-600 dark:text-red-400';
                  } else if (t.status === 'cancelled') {
                    statusLabel = 'ملغى 🚫';
                    statusCls = 'bg-red-500/10 text-red-600 dark:text-red-400';
                  }
                  
                  return (
                    <span key={t.id || idx} className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${statusCls}`}>
                      {t.team_name} ({statusLabel})
                    </span>
                  );
                })}
              </div>
            ) : (
              <span className="truncate text-xs">{task.assigned_teams.map(t => t.team_name).join(' · ')}</span>
            )}
          </div>
        )}

        {/* Assigned Employees Preview */}
        {task.assignments?.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-purple-500">👤</span>
            {task.assignments.slice(0, 4).map((a, i) => (
              <span key={a.id || i} className="text-[10px] bg-purple-500/10 text-purple-400 px-1.5 py-0.5 rounded-full font-semibold">
                {a.employee_name}
              </span>
            ))}
            {task.assignments.length > 4 && (
              <span className="text-[10px] text-purple-400/70">+{task.assignments.length - 4}</span>
            )}
          </div>
        )}

        {/* Seconds + Multiplier + Price */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {task.video_seconds} ث</span>
          {multiplier.label && (
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${multiplier.cls}`}>{multiplier.label}</span>
          )}
          {price !== null && <span className="font-bold text-[var(--text-dark-h)]">{formatIQD(price)}</span>}
        </div>

        {/* Attachments + Deadline */}
        <div className="flex items-center gap-3">
          {task.deadline && (
            <span className={`flex items-center gap-1 ${deadlineCls}`}>
              <FileText className={`w-3 h-3 ${deadlineIconCls}`} /> 
              {task.deadline}
            </span>
          )}
          {task.team_count > 1 && (
            <span className="text-yellow-500 font-bold flex items-center gap-1"><Award className="w-3 h-3" /> {task.team_count} متنافس</span>
          )}
        </div>

        {task.team_count > 1 && !['ACCEPTED', 'LOST', 'CANCELLED', 'ARCHIVED', 'REJECTED'].includes(task.status) && (
          <div className="mt-2 p-1.5 px-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-[9px] text-amber-600 dark:text-amber-400 font-bold flex items-center gap-1.5 animate-pulse">
            <span>🏆</span>
            <span>سيتم اختيار الأفضل والأسرع من المنافسة! 🚀</span>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className={`flex gap-1.5 ${compact ? 'mt-2 pt-2' : 'mt-3 pt-3'} border-t border-[var(--border-light)]`} onClick={e => e.stopPropagation()}>
        <button onClick={onView} className={`flex-1 ${compact ? 'py-1 text-[10px]' : 'py-1.5 text-xs'} rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition font-bold`}>
          <Eye className="w-3 h-3 inline ml-1" /> تفاصيل
        </button>
        {isSupervisor && task.status === 'DRAFT' && (
          <>
            <button onClick={e => { e.stopPropagation(); onEdit(); }} className={`flex-1 ${compact ? 'py-1 text-[10px]' : 'py-1.5 text-xs'} rounded-lg bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:opacity-80 transition font-bold`}>
              تعديل
            </button>
            <button onClick={e => { e.stopPropagation(); onAssign(); }} className={`flex-1 ${compact ? 'py-1 text-[10px]' : 'py-1.5 text-xs'} rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:opacity-80 transition font-bold`}>
              <Send className="w-3 h-3 inline ml-1" /> تعيين
            </button>
          </>
        )}
        {isSupervisor && task.status === 'ASSIGNED' && (!task.assignments || task.assignments.length === 0) && (
          <button onClick={e => { e.stopPropagation(); onEdit(); }} className={`flex-1 ${compact ? 'py-1 text-[10px]' : 'py-1.5 text-xs'} rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 hover:opacity-80 transition font-bold`}>
            ✏️ تعديل
          </button>
        )}
        {isTeamLeader && ['ASSIGNED', 'IN_PROGRESS', 'REVISION', 'SUBMITTED'].includes(task.status) && (
          <button onClick={onDistribute} className={`flex-1 ${compact ? 'py-1 text-[10px]' : 'py-1.5 text-xs'} rounded-lg bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 hover:opacity-80 transition font-bold`}>
            <Users className="w-3 h-3 inline ml-1" /> توزيع
          </button>
        )}
        {(isTeamLeader || isTeamMember) && ['IN_PROGRESS', 'REVISION', 'SUBMITTED', 'TL_APPROVED'].includes(task.status) && (
          <button onClick={onSubmit} className={`flex-1 ${compact ? 'py-1 text-[10px]' : 'py-1.5 text-xs'} rounded-lg ${['SUBMITTED','TL_APPROVED'].includes(task.status) ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'} hover:opacity-80 transition font-bold`}>
            <Upload className="w-3 h-3 inline ml-1" /> {['SUBMITTED','TL_APPROVED'].includes(task.status) ? 'إعادة تسليم' : 'تسليم'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Task Form Modal (Create / Edit) ──────────────────────────────────────────
function TaskFormModal({ user, task, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: task?.title || '', 
    description: task?.description || '', 
    task_links: task?.task_links || '', 
    client_id: task?.client_id || '',
    subscription_id: task?.subscription_id || '', 
    video_seconds: task?.video_seconds || '', 
    video_type: task?.video_type || 'ugc',
    priority: task?.priority || 'medium', 
    multiplier: task?.multiplier || '1', 
    deadline: task?.deadline || '',
    team_ids: task?.assigned_teams?.map(t => t.team_id) || [], 
    supervisor_employee_id: task?.supervisor_employee_id || ''
  });
  const [clients, setClients] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [briefFiles, setBriefFiles] = useState([]);
  const [checklists, setChecklists] = useState(['']);
  const [internalMembers, setInternalMembers] = useState({}); // { [teamId]: [members...] }
  const [selectedEmployees, setSelectedEmployees] = useState({}); // { [teamId]: [empId1, empId2] }

  const toggleEmployee = (teamId, empId) => {
    setSelectedEmployees(prev => {
      const current = prev[teamId] || [];
      const next = current.includes(empId)
        ? current.filter(x => x !== empId)
        : [...current, empId];
      return { ...prev, [teamId]: next };
    });
  };

  useEffect(() => {
    if (task?.assignments) {
      const initial = {};
      task.assignments.forEach(a => {
        if (!initial[a.team_id]) initial[a.team_id] = [];
        initial[a.team_id].push(a.employee_id);
      });
      setSelectedEmployees(initial);
    }
  }, [task]);

  useEffect(() => {
    getTmClients().then(d => setClients(Array.isArray(d) ? d : d.clients || [])).catch(() => {});
    getTmTeams().then(d => {
      const allTeams = Array.isArray(d) ? d : d.teams || [];
      setTeams(allTeams);
      
      // Fetch members for internal teams
      allTeams.forEach(t => {
        if (t.team_type === 'internal') {
          getTmTeamMembers(t.id).then(m => {
            setInternalMembers(prev => ({
              ...prev,
              [t.id]: m.filter(mem => mem.status === 'active')
            }));
          }).catch(() => {});
        }
      });
    }).catch(() => {});
    getSupervisorsList().then(d => setSupervisors(Array.isArray(d) ? d : d.supervisors || [])).catch(() => {});
    if (task?.client_id && task.client_id !== '0') {
      getTmSubscriptions(task.client_id).then(d => setSubscriptions(Array.isArray(d) ? d : d.subscriptions || [])).catch(() => {});
    }
  }, [task]);

  const handleClientChange = (clientId) => {
    setForm(f => ({ ...f, client_id: clientId, subscription_id: '' }));
    if (clientId && clientId !== '0') {
      getTmSubscriptions(clientId).then(d => setSubscriptions(Array.isArray(d) ? d : d.subscriptions || [])).catch(() => {});
    } else {
      setSubscriptions([]);
    }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.video_seconds) return toast.error('العنوان وعدد الثواني مطلوبان');
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description,
        task_links: form.task_links || undefined,
        video_seconds: parseInt(form.video_seconds),
        video_type: form.video_type, priority: form.priority,
        multiplier: parseFloat(form.multiplier),
        deadline: form.deadline || undefined,
        checklists: checklists.filter(c => c.trim())
      };
      if (form.client_id && form.client_id !== '0') {
        payload.client_id = form.client_id;
        payload.subscription_id = form.subscription_id || undefined;
      } else {
        payload.guest_client_name = form.client_id === '0' ? 'MS Media Internal' : form.title;
      }
      if (form.supervisor_employee_id) payload.supervisor_employee_id = parseInt(form.supervisor_employee_id);
      if (form.team_ids.length > 0) payload.team_ids = form.team_ids;
      
      let result = { id: task?.id };
      if (task) {
        await updateTmTask(task.id, payload);
      } else {
        result = await createTmTask(payload);
      }

      // Direct distribution for internal teams
      const targetTaskId = result.id || task?.id;
      if (targetTaskId) {
        for (const teamId of form.team_ids) {
          const empIds = selectedEmployees[teamId] || [];
          if (empIds.length > 0) {
            await distributeTmTask(targetTaskId, { employee_ids: empIds, team_id: teamId }).catch(() => {});
          }
        }
      }

      if (briefFiles.length > 0 && targetTaskId) {
        for (const file of briefFiles) {
          await uploadTmAttachment(targetTaskId, file, 'brief');
        }
      }

      toast.success(task ? 'تم حفظ التعديلات' : 'تم إنشاء المهمة');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={<span className="font-bold flex items-center gap-2"><Plus className="w-5 h-5 text-primary"/> إنشاء مهمة جديدة</span>} onClose={onClose} wide maxWidth="max-w-3xl">
      <div className="space-y-5 max-h-[80vh] overflow-y-auto custom-scrollbar pr-2 pb-2">
        
        {/* Title */}
        <LabeledInput label="عنوان المهمة" required>
          <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="أدخل عنوان المهمة هنا..." className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow" />
        </LabeledInput>
        
        {/* Description */}
        <LabeledInput label="الوصف / الملاحظات">
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="اكتب ملاحظات وتفاصيل المهمة هنا..." rows={3} className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow resize-none" />
        </LabeledInput>

        {/* Task Links */}
        <LabeledInput label="روابط المراجع والملاحظات (URLs)">
          <input type="text" value={form.task_links} onChange={e => setForm(f => ({ ...f, task_links: e.target.value }))} placeholder="أدخل روابط المراجع هنا (مثال: https://drive.google.com/...) تفصل بمسافة أو فاصلة" className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow" />
        </LabeledInput>

        {/* Row 1: Category & Seconds */}
        <div className="grid grid-cols-2 gap-4">
          <LabeledInput label="الوحدات (ثواني/نقاط)" required>
            <input type="text" inputMode="decimal" value={form.video_seconds} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setForm(f => ({ ...f, video_seconds: e.target.value }))} placeholder="مثال: 30" className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow" />
          </LabeledInput>
          <div>
            <label className="block text-[13px] font-bold text-[var(--text-gray)] mb-2">تصنيف المهمة</label>
            <select value={form.video_type} onChange={e => setForm(f => ({ ...f, video_type: e.target.value }))}
              className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow">
              {Object.entries({
                'ugc': { label: 'UGC' },
                'reel': { label: 'Reel' },
                'motion': { label: 'Motion' },
                'tvc': { label: 'TVC' },
                'cartoon': { label: 'Cartoon' },
                'editing': { label: 'Editing' },
                'voiceover': { label: 'Voiceover' },
                'design': { label: 'Design' }
              }).map(([k, vt]) => <option key={k} value={k}>{vt.label}</option>)}
            </select>
          </div>
        </div>

        {/* Row 2: Deadline & Priority */}
        <div className="grid grid-cols-2 gap-4">
          <LabeledInput label="الديدلاين (تاريخ التسليم)">
            <input type="datetime-local" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} dir="ltr" className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow" />
          </LabeledInput>
          <div>
            <label className="block text-[13px] font-bold text-[var(--text-gray)] mb-2">الأهمية</label>
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
              className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow">
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>

        {/* Multiplier (Owner Only) */}
        {user?.role === 'owner' && (
          <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 p-3 rounded-xl animate-fade-in">
            <label className="block text-[13px] font-bold text-primary mb-2 flex items-center gap-1"><Award className="w-4 h-4"/> مضاعفة النقاط والأرباح (خاص بالمالك)</label>
            <select value={form.multiplier} onChange={e => setForm(f => ({ ...f, multiplier: e.target.value }))}
              className="w-full p-2 rounded-xl border border-primary/30 bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm">
              <option value="1">1x (عادي)</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x (مضاعف)</option>
              <option value="2.5">2.5x</option>
              <option value="3">3x (ثلاثي ⭐)</option>
            </select>
          </div>
        )}

        {/* Row 3: Assign To & Client */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[13px] font-bold text-[var(--text-gray)] mb-2">العميل (اختياري)</label>
            <select value={form.client_id} onChange={e => handleClientChange(e.target.value)}
              className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow">
              <option value="">-- بدون عميل --</option>
              <option value="0">MS Media (Internal / عابر)</option>
              <optgroup label="العملاء المسجلين">
                {Array.isArray(clients) && clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            </select>
            {form.client_id && form.client_id !== '0' && (
              <div className="mt-2">
                <select value={form.subscription_id} onChange={e => setForm(f => ({ ...f, subscription_id: e.target.value }))}
                  className="w-full p-2 rounded-lg border border-[var(--border-light)] bg-[var(--bg-flat-50)] text-[var(--text-dark-h)] text-[11px] focus:outline-none">
                  <option value="">-- غير مرتبط باشتراك --</option>
                  {Array.isArray(subscriptions) && subscriptions.map(s => <option key={s.id} value={s.id}>#{s.id} — {s.remaining_seconds || 0} ث متبقية</option>)}
                </select>
              </div>
            )}
          </div>
          <div>
            <label className="block text-[13px] font-bold text-[var(--text-gray)] mb-2">تعيين إلى فريق (يمكن اختيار أكثر من فريق)</label>
            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto px-2 py-1.5 flex-1 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e]">
              <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 rounded">
                <input type="checkbox" checked={form.team_ids.length === 0} onChange={() => setForm(f => ({ ...f, team_ids: [] }))} className="accent-primary" />
                <span className="text-sm text-[var(--text-dark-h)]">-- بدون فريق --</span>
              </label>
              {Array.isArray(teams) && teams.map(t => {
                const isInternal = t.team_type === 'internal';
                const isChecked = form.team_ids.includes(t.id);
                const membersList = internalMembers[t.id] || [];
                return (
                  <div key={t.id} className="space-y-1">
                    <label className="flex items-center gap-2 cursor-pointer p-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 rounded border-t border-[var(--border-light)]">
                      <input type="checkbox" checked={isChecked} onChange={e => {
                        if (e.target.checked) setForm(f => ({ ...f, team_ids: [...f.team_ids, t.id] }));
                        else setForm(f => ({ ...f, team_ids: f.team_ids.filter(id => id !== t.id) }));
                      }} className="accent-primary" />
                      <span className="text-sm text-[var(--text-dark-h)] flex items-center gap-1.5">
                        {t.name}
                        {isInternal && (
                          <span className="text-[9px] bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-1.5 py-0.5 rounded font-bold">داخلي</span>
                        )}
                      </span>
                    </label>
                    {isInternal && isChecked && membersList.length > 0 && (
                      <div className="mr-6 p-2 bg-gray-50 dark:bg-white/5 rounded-lg border border-[var(--border-light)] space-y-1.5">
                        <p className="text-[11px] font-bold text-[var(--text-gray)]">اختر الموظفين للتعيين المباشر:</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {membersList.map(m => {
                            const isEmpSelected = (selectedEmployees[t.id] || []).includes(m.employee_id);
                            return (
                              <label key={m.employee_id} className="flex items-center gap-1.5 text-xs cursor-pointer text-[var(--text-dark-h)]">
                                <input
                                  type="checkbox"
                                  checked={isEmpSelected}
                                  onChange={() => toggleEmployee(t.id, m.employee_id)}
                                  className="accent-primary"
                                />
                                <span>{m.employee_name}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 4: Supervisor */}
        <div>
          <label className="block text-[13px] font-bold text-[var(--text-gray)] mb-2">تعيين متابع (Supervisor)</label>
          <select value={form.supervisor_employee_id} onChange={e => setForm(f => ({ ...f, supervisor_employee_id: e.target.value }))}
            className="w-full p-2.5 rounded-xl border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow">
            <option value="">-- اختياري --</option>
            {Array.isArray(supervisors) && supervisors.map(s => <option key={s.id} value={s.id}>{s.name || s.username}</option>)}
          </select>
        </div>

        {/* Checklists (To-Do List) */}
        <div className="mt-6 pt-4 border-t border-[var(--border-light)]">
          <label className="flex items-center gap-2 text-[13px] font-bold text-[var(--text-gray)] mb-3">
            <CheckCircle className="w-4 h-4"/> المهام المطلوبة (Checklist)
          </label>
          {checklists.length === 0 && <p className="text-xs text-[var(--text-muted)] text-center my-4">لا توجد عناصر بالقائمة الفرعية</p>}
          <div className="space-y-2">
            {checklists.map((c, idx) => (
              <div key={idx} className="flex gap-2 group">
                <input type="text" value={c} onChange={e => {
                  const newC = [...checklists]; newC[idx] = e.target.value; setChecklists(newC);
                }} placeholder="أضف خطوة فرعية..." className="flex-1 p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow" />
                <button type="button" onClick={() => setChecklists(checklists.filter((_, i) => i !== idx))} className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-opacity">✖</button>
              </div>
            ))}
            <button type="button" onClick={() => setChecklists([...checklists, ''])} className="px-4 py-2 mt-2 bg-primary/5 text-primary hover:bg-primary/10 text-[13px] font-bold rounded-xl border border-primary/20 transition-colors">
              إضافة
            </button>
          </div>
        </div>

        {/* Attachments */}
        <div className="mt-6 pt-4 border-t border-[var(--border-light)]">
          <label className="flex items-center gap-2 text-[13px] font-bold text-[var(--text-gray)] mb-3">
            <Paperclip className="w-4 h-4"/> مرفقات عامة للمهمة (البريف)
          </label>
          <div className="border border-dashed border-primary/40 p-4 rounded-xl text-center bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer relative">
            <input type="file" multiple onChange={e => setBriefFiles([...briefFiles, ...Array.from(e.target.files)])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
            <Upload className="w-5 h-5 text-primary mx-auto mb-2 opacity-80" />
            <p className="text-[13px] font-bold text-primary">رفع مرفق عام (بريف)...</p>
          </div>
          {briefFiles.length > 0 && (
            <div className="mt-3 space-y-2">
              {briefFiles.map((f, i) => (
                <div key={i} className="text-xs bg-[var(--bg-flat-50)] p-2.5 rounded-lg border border-[var(--border-light)] flex justify-between items-center group">
                  <span className="truncate pr-2 text-[var(--text-dark-h)] font-semibold">{f.name}</span>
                  <button onClick={() => setBriefFiles(briefFiles.filter((_, idx) => idx !== i))} className="text-[var(--text-gray)] hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Emulate Staff Submission Disabled State */}
        <div className="mt-6 pt-4 border-t border-[var(--border-light)] opacity-60 pointer-events-none grayscale">
          <label className="flex items-center gap-2 text-[13px] font-bold text-green-600 dark:text-green-400 mb-3">
            <CheckCircle className="w-4 h-4"/> مرفقات الموظف (التسليم)
          </label>
          <div className="border border-dashed border-green-600/40 p-4 rounded-xl text-center bg-green-600/5">
            <Upload className="w-5 h-5 text-green-600 mx-auto mb-2 opacity-80" />
            <p className="text-[13px] font-bold text-green-600">رفع ملف تسليم (بعد تعيين المهمة)...</p>
          </div>
        </div>

      </div>

      <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-[var(--border-light)]">
        <button onClick={handleSubmit} disabled={saving} className="px-6 py-2.5 text-sm font-bold text-white bg-primary hover:bg-[var(--primary-dark)] rounded-xl transition-colors disabled:opacity-50">
          {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
        </button>
        <button onClick={onClose} className="px-6 py-2.5 text-sm font-bold text-[var(--text-gray)] bg-[var(--bg-flat-50)] hover:bg-[var(--border-light)] rounded-xl transition-colors border border-[var(--border-light)]">إلغاء</button>
      </div>
    </Modal>
  );
}

// ─── Assign Modal ─────────────────────────────────────────────────────────────
function AssignModal({ task, teams, onClose, onDone }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [internalMembers, setInternalMembers] = useState({}); // { [teamId]: [members...] }
  const [selectedEmployees, setSelectedEmployees] = useState({}); // { [teamId]: [empId1, empId2] }

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const toggleEmployee = (teamId, empId) => {
    setSelectedEmployees(prev => {
      const current = prev[teamId] || [];
      const next = current.includes(empId)
        ? current.filter(x => x !== empId)
        : [...current, empId];
      return { ...prev, [teamId]: next };
    });
  };

  useEffect(() => {
    teams.forEach(t => {
      if (t.team_type === 'internal') {
        getTmTeamMembers(t.id)
          .then(m => {
            setInternalMembers(prev => ({
              ...prev,
              [t.id]: m.filter(mem => mem.status === 'active')
            }));
          })
          .catch(() => {});
      }
    });
  }, [teams]);

  const handleAssign = async () => {
    if (selected.length === 0) return toast.error('اختر فريق واحد على الأقل');
    setSaving(true);
    try {
      await assignTmTask(task.id, { team_ids: selected });
      
      // Auto-distribute to chosen employees for internal teams
      for (const teamId of selected) {
        const empIds = selectedEmployees[teamId] || [];
        if (empIds.length > 0) {
          await distributeTmTask(task.id, { employee_ids: empIds, team_id: teamId });
        }
      }

      toast.success('تم تعيين المهمة وتوزيعها بنجاح');
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ في التعيين'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`تعيين فرق — ${task.title}`} onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto custom-scrollbar px-1">
        <p className="text-sm text-[var(--text-gray)]">اختر الفرق المتنافسة (يمكن اختيار أكثر من فريق):</p>
        {teams.map(t => {
          const isInternal = t.team_type === 'internal';
          const isSelected = selected.includes(t.id);
          const membersList = internalMembers[t.id] || [];
          return (
            <div key={t.id} className="space-y-2">
              <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                isSelected ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-[var(--border-light)] hover:bg-[var(--bg-flat-50)]'
              }`}>
                <input type="checkbox" checked={isSelected} onChange={() => toggle(t.id)} className="accent-primary" />
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <p className="font-bold text-sm text-[var(--text-dark-h)]">{t.name}</p>
                    {isInternal && (
                      <span className="text-[10px] bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full font-bold">فريق داخلي</span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-muted)]">
                    {isInternal ? 'تابع للوكالة' : `القائد: ${t.leader_name}`} — {isInternal ? `${membersList.length} موظف` : `${t.member_count || 0} عضو`}
                  </p>
                </div>
              </label>

              {isInternal && isSelected && membersList.length > 0 && (
                <div className="mr-8 p-3 bg-gray-50 dark:bg-white/5 rounded-xl border border-[var(--border-light)] space-y-2">
                  <p className="text-xs font-bold text-[var(--text-gray)]">اختر الموظفين لتعيينهم مباشرة للمهمة:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {membersList.map(m => {
                      const isEmpSelected = (selectedEmployees[t.id] || []).includes(m.employee_id);
                      return (
                        <label key={m.employee_id} className="flex items-center gap-2 text-xs cursor-pointer text-[var(--text-dark-h)]">
                          <input
                            type="checkbox"
                            checked={isEmpSelected}
                            onChange={() => toggleEmployee(t.id, m.employee_id)}
                            className="accent-primary"
                          />
                          <span>{m.employee_name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {selected.length > 1 && (
          <p className="text-xs text-yellow-600 dark:text-yellow-400">⚠️ تعيين أكثر من فريق = نظام مسابقة</p>
        )}
        <button onClick={handleAssign} disabled={saving || selected.length === 0}
          className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 mt-4">
          {saving ? 'جاري التعيين والتوزيع...' : `تعيين لـ ${selected.length} فريق`}
        </button>
      </div>
    </Modal>
  );
}

// ─── Distribute Modal ─────────────────────────────────────────────────────────
function DistributeModal({ task, user, initialTeamId = null, onClose, onDone }) {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [splits, setSplits] = useState({});
  const [customSplit, setCustomSplit] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        let activeTeamId = initialTeamId;
        if (!activeTeamId) {
          const teams = await getTmTeams();
          const myTeam = teams.find(t => String(t.leader_employee_id) === String(user.employee_id));
          if (myTeam) {
            activeTeamId = myTeam.id;
          }
        }
        
        if (activeTeamId) {
          setTeamId(activeTeamId);
          const m = await getTmTeamMembers(activeTeamId);
          setMembers(m.filter(mem => mem.status === 'active'));
          
          // Pre-select currently assigned members of this team
          const currentlyAssigned = task.assignments
            ?.filter(a => a.team_id === activeTeamId)
            ?.map(a => a.employee_id) || [];
          setSelected(currentlyAssigned);
          
          // Pre-populate splits if they exist
          const initialSplits = {};
          task.assignments
            ?.filter(a => a.team_id === activeTeamId)
            ?.forEach(a => {
              if (a.share_percentage !== null) {
                initialSplits[a.employee_id] = a.share_percentage;
              }
            });
          setSplits(initialSplits);
          if (Object.keys(initialSplits).length > 0) {
            setCustomSplit(true);
          }
        }
      } catch (e) { toast.error('خطأ في تحميل الأعضاء'); }
    })();
  }, [user.employee_id, initialTeamId, task.assignments]);

  const toggle = (id) => {
    setSelected(s => {
      const next = s.includes(id) ? s.filter(x => x !== id) : [...s, id];
      // Automatically initialize split values equally when selection changes
      if (next.length > 0) {
        const equalPct = Math.round(100 / next.length);
        const nextSplits = {};
        next.forEach((empId, idx) => {
          nextSplits[empId] = idx === next.length - 1 
            ? 100 - (equalPct * (next.length - 1)) 
            : equalPct;
        });
        setSplits(nextSplits);
      } else {
        setSplits({});
      }
      return next;
    });
  };

  const handleSplitChange = (empId, val) => {
    setSplits(prev => ({ ...prev, [empId]: val }));
  };

  const handleDistribute = async () => {
    if (selected.length === 0) return toast.error('اختر موظف واحد على الأقل');
    
    let payload = { employee_ids: selected, team_id: teamId };
    
    if (selected.length > 1 && customSplit) {
      const vals = selected.map(id => parseFloat(splits[id] || 0));
      const sum = vals.reduce((a, b) => a + b, 0);
      if (Math.abs(sum - 100) > 0.01) {
        return toast.error('مجموع نسب التوزيع يجب أن يساوي 100% بالضبط');
      }
      payload.splits = {};
      selected.forEach(id => {
        payload.splits[id] = parseFloat(splits[id] || 0);
      });
    }

    setSaving(true);
    try {
      await distributeTmTask(task.id, payload);
      toast.success('تم توزيع المهمة بنجاح');
      onDone();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ في التوزيع'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`توزيع المهمة — ${task.title}`} onClose={onClose}>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar pr-1">
        <p className="text-sm text-[var(--text-gray)]">اختر الموظفين لتوزيع المهمة عليهم:</p>
        
        {members.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] text-center py-4">لا يوجد أعضاء مفعّلين</p>
        ) : members.map(m => {
          const isSel = selected.includes(m.employee_id);
          return (
            <div key={m.id} className={`p-3 rounded-xl border transition-all ${
              isSel ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-[var(--border-light)] hover:bg-[var(--bg-flat-50)]'
            }`}>
              <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <input type="checkbox" checked={isSel} onChange={() => toggle(m.employee_id)} className="accent-primary" />
                  <div>
                    <p className="font-bold text-sm text-[var(--text-dark-h)]">
                      {m.is_leader === 1 ? '👑 ' : ''}{m.employee_name}
                    </p>
                    <p className="text-[10px] text-[var(--text-muted)]">{m.employee_role === 'team_leader' ? 'قائد الفريق' : 'عضو'}</p>
                  </div>
                </label>
                {m.active_tasks_count > 0 ? (
                  <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 whitespace-nowrap">
                    🔥 {m.active_tasks_count} مهام
                  </span>
                ) : (
                  <span className="text-[10px] font-bold px-2 py-1 rounded-lg bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 whitespace-nowrap">
                    متاح
                  </span>
                )}
              </div>

              {isSel && selected.length > 1 && customSplit && (
                <div className="mt-3 pt-3 border-t border-dashed border-[var(--border-light)] flex items-center gap-2">
                  <span className="text-xs text-[var(--text-gray)]">نسبة المساهمة المالية والزمنية:</span>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <input type="number" min="0" max="100" value={splits[m.employee_id] !== undefined ? splits[m.employee_id] : ''} onChange={e => handleSplitChange(m.employee_id, e.target.value)}
                      className="w-16 p-1.5 rounded-lg border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-xs text-center outline-none focus:border-primary" />
                    <span className="text-xs text-[var(--text-muted)]">%</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {selected.length > 1 && (
          <label className="flex items-center gap-2 p-2 rounded-xl bg-primary/5 border border-primary/20 cursor-pointer animate-fade-in mt-2">
            <input type="checkbox" checked={customSplit} onChange={e => setCustomSplit(e.target.checked)} className="accent-primary" />
            <span className="text-xs font-bold text-primary">⚙️ تفعيل تقسيم نسب المساهمة (توزيع مخصص للثواني والأرباح)</span>
          </label>
        )}

        {selected.length > 1 && customSplit && (
          <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/10 text-xs flex justify-between items-center text-blue-700 dark:text-blue-300 font-bold border border-blue-100 dark:border-blue-900/30">
            <span>مجموع التوزيع الحالي:</span>
            <span className={Math.abs(selected.reduce((a, b) => a + parseFloat(splits[b] || 0), 0) - 100) < 0.01 ? 'text-green-600 font-bold' : 'text-red-500 font-bold'}>
              {selected.reduce((a, b) => a + parseFloat(splits[b] || 0), 0)}% / 100%
            </span>
          </div>
        )}

        <button onClick={handleDistribute} disabled={saving || selected.length === 0}
          className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 mt-2">
          {saving ? 'جاري التوزيع...' : `توزيع على ${selected.length} موظف`}
        </button>
      </div>
    </Modal>
  );
}

// ─── Submit Modal ─────────────────────────────────────────────────────────────
function SubmitModal({ task, user, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = React.useRef(null);

  const handleAddFiles = (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      const unique = newFiles.filter(f => !existingNames.has(f.name));
      if (unique.length < newFiles.length) {
        toast(`${newFiles.length - unique.length} ملف مكرر تم تجاهله`, { icon: 'ℹ️' });
      }
      return [...prev, ...unique];
    });
    // Reset input so the same file can be re-selected if removed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0) return toast.error('يرجى إرفاق ملفات التسليم');
    setSaving(true);
    setUploadProgress(0);
    try {
      await createTmSubmission({ task_id: task.id }, files, (progressEvent) => {
        const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(pct);
      });
      toast.success('تم التسليم بنجاح');
      onDone();
    } catch (e) {
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        toast.error('انتهت مدة الرفع — حاول ملف أصغر أو تحقق من الإنترنت');
      } else if (e.response?.status === 401) {
        toast.error('انتهت الجلسة — يرجى تسجيل الدخول مرة أخرى');
      } else {
        toast.error(e.response?.data?.error || 'خطأ في رفع الملفات');
      }
    }
    finally { setSaving(false); setUploadProgress(0); }
  };

  const isResubmit = ['SUBMITTED', 'TL_APPROVED'].includes(task.status);

  return (
    <Modal title={isResubmit ? `إعادة تسليم — ${task.title}` : `تسليم — ${task.title}`} onClose={onClose}>
      <div className="space-y-4">
        {isResubmit && (
          <div className="p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 text-sm text-orange-700 dark:text-orange-300 font-bold">
            ⚠️ سيتم سحب التسليمة السابقة واستبدالها بالملفات الجديدة
          </div>
        )}
        {task.team_count > 1 && (
          <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 text-xs text-amber-700 dark:text-amber-300 font-bold flex items-center gap-2 animate-pulse">
            <span>🏆</span>
            <div>
              <p className="font-bold text-[13px] text-amber-800 dark:text-amber-200">نظام المنافسة نشط!</p>
              <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">سيتم اختيار الأفضل والأسرع من المنافسة 🚀⭐</p>
            </div>
          </div>
        )}
        <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-700 dark:text-blue-300">
          <Clock className="w-4 h-4 inline ml-1" /> المطلوب: {formatSeconds(task.video_seconds)} — نوع: {task.video_type}
          {(task.multiplier || 1) > 1 && <span className="mr-2 font-bold">× {task.multiplier}</span>}
        </div>
        <div>
          <label className="block text-xs font-bold text-[var(--text-gray)] mb-2">ملفات التسليم *</label>
          <div className="border-2 border-dashed border-[var(--border-light)] rounded-xl p-4 text-center hover:border-green-500/50 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}>
            <input ref={fileInputRef} type="file" multiple onChange={handleAddFiles} className="hidden" />
            <p className="text-sm text-[var(--text-gray)]">📁 اضغط لاختيار ملفات{files.length > 0 ? ' إضافية' : ''}</p>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">يمكنك اختيار ملفات متعددة — ستُضاف للقائمة تلقائياً</p>
          </div>
          {files.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-[var(--text-dark-h)]">📎 {files.length} ملف مرفق — {(files.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(1)} MB</p>
                <button onClick={() => setFiles([])} className="text-[10px] text-red-500 hover:text-red-600 font-bold">مسح الكل</button>
              </div>
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="flex items-center justify-between p-2 rounded-lg bg-green-50 dark:bg-green-900/10 text-xs">
                  <span className="text-green-700 dark:text-green-400 truncate flex-1 ml-2">{f.name}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[var(--text-muted)]">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                    <button onClick={() => handleRemoveFile(i)} className="text-red-400 hover:text-red-600 transition" title="إزالة">✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Upload Progress Bar */}
        {saving && uploadProgress > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[var(--text-gray)] font-bold">⬆️ جاري الرفع...</span>
              <span className="text-green-600 dark:text-green-400 font-bold">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
              <div className="bg-green-500 h-2.5 rounded-full transition-all duration-300 ease-out" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}
        <button onClick={handleSubmit} disabled={saving}
          className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-all disabled:opacity-50">
          {saving ? (uploadProgress > 0 ? `⬆️ جاري الرفع ${uploadProgress}%` : 'جاري التحضير...') : `📤 تسليم المهمة (${files.length} ملف)`}
        </button>
      </div>
    </Modal>
  );
}


// ─── Task Detail Modal ────────────────────────────────────────────────────────
function TaskDetailModal({ task, user, teams, ratePerSecond = 500, onClose, onRefresh, onReload }) {
  const isOwner = ['owner', 'admin'].includes(user.role);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isSupervisor = user.role === 'supervisor' || isOwner;
  const isTeamLeader = user.role === 'team_leader';
  const isTeamMember = user.role === 'team_member';
  const [tab, setTab] = useState('info');
  const [submissions, setSubmissions] = useState([]);
  const [reviewNotes, setReviewNotes] = useState('');
  const [transferTeamId, setTransferTeamId] = useState('');
  const [showTransferUI, setShowTransferUI] = useState(null);
  const [saving, setSaving] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');

  const [reassignFrom, setReassignFrom] = useState(null); // { employee_id, team_id, employee_name }
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedNewMember, setSelectedNewMember] = useState('');

  const renderClickableLinks = (linksString) => {
    if (!linksString) return null;
    const urls = linksString.split(/[\s,;\n]+/).filter(url => {
      return url.trim().match(/^(https?:\/\/|www\.)[^\s]+$/i) || url.trim().match(/[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i);
    });
    
    if (urls.length === 0) return null;

    return (
      <div className="p-3 rounded-xl bg-blue-50/50 dark:bg-blue-950/10 border border-blue-100 dark:border-blue-900/20 space-y-2 mt-2">
        <span className="text-[11px] text-[var(--text-muted)] font-bold block">🔗 روابط المراجع والملاحظات:</span>
        <div className="flex flex-col gap-1.5">
          {urls.map((url, i) => {
            let href = url.trim();
            if (!href.startsWith('http://') && !href.startsWith('https://')) {
              href = 'https://' + href;
            }
            let label = url.trim();
            if (label.length > 55) {
              label = label.substring(0, 52) + '...';
            }
            return (
              <a key={i} href={href} target="_blank" rel="noopener noreferrer" 
                className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 text-xs font-semibold hover:underline break-all">
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                <span>{label}</span>
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (reassignFrom) {
      getTmTeamMembers(reassignFrom.team_id)
        .then(m => setTeamMembers(m.filter(mem => mem.status === 'active' && mem.employee_id !== reassignFrom.employee_id)))
        .catch(() => {});
    } else {
      setTeamMembers([]);
      setSelectedNewMember('');
    }
  }, [reassignFrom]);

  const handleUnassign = async (empId, teamId, empName) => {
    if (!window.confirm(`هل أنت متأكد من إلغاء تعيين الموظف "${empName}" من هذه المهمة؟`)) return;
    setSaving(true);
    try {
      await unassignTmTask(task.id, { employee_id: empId, team_id: teamId });
      toast.success('تم إلغاء التعيين بنجاح');
      onReload();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ في إلغاء التعيين'); }
    finally { setSaving(false); }
  };

  const handleReassignSubmit = async () => {
    if (!selectedNewMember) return toast.error('يرجى اختيار الموظف البديل');
    setSaving(true);
    try {
      await reassignTmTask(task.id, {
        from_employee_id: reassignFrom.employee_id,
        to_employee_id: parseInt(selectedNewMember),
        team_id: reassignFrom.team_id
      });
      toast.success('تم نقل المهمة بنجاح');
      setReassignFrom(null);
      onReload();
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ في النقل'); }
    finally { setSaving(false); }
  };

  const multiplier = MULTIPLIER_CONFIG[task.multiplier] || MULTIPLIER_CONFIG[1];
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;

  useEffect(() => {
    if (tab === 'review') loadSubmissions();
  }, [tab]);

  const loadSubmissions = async () => {
    try { setSubmissions(await getTmSubmissions(task.id)); } catch (e) {}
  };

  const handleTLReview = async (subId, status) => {
    const msg = status === 'approved' ? 'هل أنت متأكد من الموافقة على التسليم؟' : 'هل أنت متأكد من إرجاع التسليم للتعديل؟';
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      await reviewTmSubmissionTL(subId, { status, notes: reviewNotes });
      toast.success(status === 'approved' ? 'تمت الموافقة' : 'تم إرجاع التسليم');
      setReviewNotes(''); loadSubmissions(); onReload(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  const handleSVReview = async (subId, status) => {
    const msgs = { accepted: 'هل أنت متأكد من القبول النهائي؟ سيتم احتساب الأرباح.', revision: 'هل أنت متأكد من إرجاع التسليم للتعديل؟', rejected: 'هل أنت متأكد من الرفض النهائي؟' };
    if (!window.confirm(msgs[status] || 'هل أنت متأكد؟')) return;
    setSaving(true);
    try {
      await reviewTmSubmissionSV(subId, { status, notes: reviewNotes });
      toast.success(status === 'accepted' ? '✅ تم القبول!' : status === 'revision' ? 'تم الإرجاع' : 'تم الرفض');
      setReviewNotes(''); loadSubmissions(); onReload(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  const handleRejectTask = async () => {
    if (!rejectNotes.trim()) return toast.error('يرجى كتابة سبب الرفض');
    if (!window.confirm('هل أنت متأكد من رفض هذه المهمة نهائياً وإحالتها للأرشيف؟')) return;
    setSaving(true);
    try {
      await rejectTmTask(task.id, { reason: rejectNotes.trim() });
      toast.success('❌ تم رفض المهمة وتحويلها للأرشيف');
      setRejectNotes('');
      onReload();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'خطأ أثناء رفض المهمة');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenTask = async () => {
    if (!window.confirm('هل أنت متأكد من إلغاء الرفض وإعادة فتح هذه المهمة للتنفيذ مرة أخرى؟')) return;
    setSaving(true);
    try {
      await reopenTmTask(task.id);
      toast.success('🔓 تم إعادة فتح المهمة بنجاح');
      onReload();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'خطأ أثناء إعادة فتح المهمة');
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async (subId) => {
    if (!transferTeamId) return toast.error('اختر فريق');
    setSaving(true);
    try {
      await transferTmSubmission(subId, { to_team_id: parseInt(transferTeamId), reason: reviewNotes });
      toast.success('تم النقل'); onReload(); onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
    finally { setSaving(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('هل أنت متأكد من إلغاء المهمة؟')) return;
    try { await cancelTmTask(task.id, {}); toast.success('تم الإلغاء'); onReload(); onClose(); }
    catch (e) { toast.error(e.response?.data?.error || 'خطأ'); }
  };

  const handleArchive = async () => {
    if (!window.confirm('هل تريد ترحيل هذه المهمة للأرشيف النهائي؟')) return;
    try { await archiveTmTask(task.id); toast.success('تم ترحيل المهمة بنجاح'); onReload(); onClose(); }
    catch (e) { toast.error(e.response?.data?.error || 'تعذر الأرشفة'); }
  };

  const handleDelete = async () => {
    try { await deleteTmTask(task.id); toast.success('تم حذف المهمة نهائياً'); onReload(); onClose(); }
    catch (e) { toast.error(e.response?.data?.error || 'تعذر الحذف'); }
  };

  const handleToggleChecklist = async (checkId, currentVal) => {
    try {
      await toggleTmChecklist(checkId, !currentVal);
      onReload();
      onRefresh();
    } catch (e) { toast.error('خطأ في تحديث الـ Checklist'); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSaving(true);
    try {
      await addTmComment(task.id, commentText);
      setCommentText('');
      onRefresh();
    } catch (e) { toast.error('خطأ في إضافة التعليق'); }
    finally { setSaving(false); }
  };

  const tabs = [
    { key: 'info', label: 'التفاصيل', icon: FileText },
    { key: 'teams', label: 'الفرق', icon: Users },
    { key: 'attachments', label: 'المرفقات', icon: Paperclip },
    { key: 'review', label: 'المراجعة', icon: CheckCircle },
    { key: 'comments', label: 'التعليقات', icon: MessageSquare },
    { key: 'activity', label: 'السجل', icon: Activity },
  ];

  return (
    <Modal title={`${formatTaskCode(task.id)} - ${task.title}`} onClose={onClose} wide>
      <div className="space-y-4">
        {/* Header Meta */}
        <div className="flex flex-wrap gap-2 items-center">
          <StatusBadge status={task.status} />
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${priority.cls}`}>{priority.label}</span>
          <span className="text-xs bg-gray-100 dark:bg-gray-800 text-[var(--text-gray)] px-2 py-1 rounded-lg">{task.video_seconds} ث</span>
          {multiplier.label && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${multiplier.cls}`}>{multiplier.label}</span>}
          {(task.display_client_name || task.client_name || task.guest_client_name) && (
            <span className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-lg">
              {task.display_client_name || task.client_name || task.guest_client_name}
              {!task.client_id && task.guest_client_name && ' 🚶'}
            </span>
          )}
          {task.deadline && <span className="text-xs text-[var(--text-muted)]">📅 {task.deadline}</span>}
        </div>

        {task.status === 'REJECTED' && (
          <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-400 font-bold flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <p className="flex items-center gap-1.5 text-sm font-black text-red-600 dark:text-red-300">
                <XCircle className="w-4 h-4 shrink-0" /> تم رفض هذه المهمة
              </p>
              {task.reject_reason && <p className="font-semibold text-xs mt-1 text-[var(--text-dark-h)]">سبب الرفض: {task.reject_reason}</p>}
            </div>
            {isOwner && (
              <button
                disabled={saving}
                onClick={handleReopenTask}
                className="self-start sm:self-center px-3 py-1.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white text-[11px] font-black rounded-lg transition-all flex items-center gap-1 shadow-md shadow-red-500/10 shrink-0"
              >
                <RotateCcw className="w-3.5 h-3.5 shrink-0" /> إلغاء الرفض وإعادة الفتح 🔓
              </button>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1.5 border-b border-[var(--border-light)] pb-2 overflow-x-auto">
          {tabs.map(t => {
            const TabIcon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all whitespace-nowrap ${
                  tab === t.key ? 'bg-primary text-white' : 'text-[var(--text-gray)] hover:bg-[var(--bg-flat-50)]'
                }`}>
                <TabIcon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Tab: Info ── */}
        {tab === 'info' && (
          <div className="space-y-3 text-sm">
            {task.description && <p className="text-[var(--text-dark-s)] whitespace-pre-wrap">{task.description}</p>}
            {renderClickableLinks(task.task_links)}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-2.5 rounded-lg bg-[var(--bg-flat-50)]"><span className="text-[var(--text-muted)]">الأولوية:</span> <span className="font-bold">{priority.label}</span></div>
              <div className="p-2.5 rounded-lg bg-[var(--bg-flat-50)]"><span className="text-[var(--text-muted)]">الموعد:</span> <span className="font-bold">{task.deadline || '—'}</span></div>
              <div className="p-2.5 rounded-lg bg-[var(--bg-flat-50)]"><span className="text-[var(--text-muted)]">المنشئ:</span> <span className="font-bold">{task.creator_name || '—'}</span></div>
              <div className="p-2.5 rounded-lg bg-[var(--bg-flat-50)]"><span className="text-[var(--text-muted)]">النوع:</span> <span className="font-bold">{task.video_type}</span></div>
              <div className="p-2.5 rounded-lg bg-[var(--bg-flat-50)]"><span className="text-[var(--text-muted)]">المعامل:</span> <span className="font-bold">{task.multiplier || 1}x</span></div>
              {!isTeamMember && <div className="p-2.5 rounded-lg bg-[var(--bg-flat-50)]"><span className="text-[var(--text-muted)]">القيمة:</span> <span className="font-bold text-green-600">{formatIQD((task.video_seconds || 0) * (task.multiplier || 1) * ratePerSecond)}</span></div>}
            </div>
            {task.sub_remaining !== undefined && (
              <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/10 text-xs">
                الاشتراك: {task.package_name || '—'} — <span className="font-bold">{task.sub_remaining} ث متبقية</span>
              </div>
            )}
            {task.team_count > 1 && (
              <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 text-xs text-amber-700 dark:text-amber-300 font-bold flex items-center gap-2 animate-pulse mt-3">
                <span>🏆</span>
                <div>
                  <p className="font-bold text-[13px] text-amber-800 dark:text-amber-200">نظام المنافسة نشط!</p>
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">سيتم اختيار الأفضل والأسرع من المنافسة 🚀⭐</p>
                </div>
              </div>
            )}
            {/* State Actions: Archive, Cancel, Delete */}
            <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-4 border-t border-[var(--border-light)]">
              {isSupervisor && task.status === 'ACCEPTED' && (
                <button onClick={handleArchive} className="flex-1 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition font-bold">
                  <Archive className="w-4 h-4 inline ml-1" /> ترحيل للأرشيف
                </button>
              )}
              {isSupervisor && !['ACCEPTED', 'ARCHIVED', 'CANCELLED', 'REJECTED'].includes(task.status) && (
                <button onClick={handleCancel} className="flex-1 py-2 text-sm text-yellow-600 border border-yellow-200 dark:border-yellow-900 rounded-xl hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition font-bold">
                  <XCircle className="w-4 h-4 inline ml-1" /> إلغاء المهمة
                </button>
              )}
              {isOwner && !confirmDelete && (
                <button onClick={() => setConfirmDelete(true)} className="flex-1 py-2 text-sm text-red-600 border border-red-300 dark:border-red-900 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition font-bold">
                  <Trash2 className="w-4 h-4 inline ml-1" /> حذف نهائي
                </button>
              )}
              {isOwner && confirmDelete && (
                <div className="flex-1 flex flex-col gap-2">
                  <span className="text-xs text-red-600 font-bold text-center">هل أنت متأكد نهائياً؟ لا يمكن التراجع!</span>
                  <div className="flex gap-2">
                    <button onClick={handleDelete} className="flex-1 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 transition font-bold">
                      نعم، احذف
                    </button>
                    <button onClick={() => setConfirmDelete(false)} className="flex-1 py-2 text-sm text-gray-700 bg-gray-200 rounded-xl hover:bg-gray-300 transition font-bold">
                      إلغاء
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Checklists Render */}
            {task.checklists && task.checklists.length > 0 && (
               <div className="mt-4 border-t border-[var(--border-light)] pt-4">
                 <h4 className="font-bold text-sm text-[var(--text-dark-h)] mb-3">قائمة المهام الفرعية (Checklist)</h4>
                 <div className="space-y-2">
                   {task.checklists.map(c => (
                     <label key={c.id} className="flex flex-row items-center cursor-pointer gap-2 select-none group">
                       <input type="checkbox" checked={c.is_completed === 1}
                         onChange={() => handleToggleChecklist(c.id, c.is_completed === 1)}
                         className="w-4 h-4 rounded text-primary bg-[var(--input-bg)] border-[var(--border-light)] focus:ring-primary focus:ring-2 cursor-pointer" />
                         <span className={`text-sm ${c.is_completed === 1 ? 'line-through text-[var(--text-muted)]' : 'text-[var(--text-dark-h)] group-hover:text-primary transition-colors'}`}>
                           {c.content}
                         </span>
                     </label>
                   ))}
                 </div>
               </div>
            )}
          </div>
        )}

        {/* ── Tab: Teams ── */}
        {tab === 'teams' && (
          <div className="space-y-3">
            {reassignFrom && (
              <div className="p-4 rounded-xl border border-blue-500/20 bg-blue-50/50 dark:bg-blue-950/20 mt-1 mb-3 space-y-3 animate-fade-in">
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1">🔄 نقل المهمة من الموظف: {reassignFrom.employee_name}</h4>
                  <button onClick={() => setReassignFrom(null)} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400">إلغاء</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-[var(--text-gray)] mb-1 font-bold">اختر الموظف البديل:</label>
                    <select value={selectedNewMember} onChange={e => setSelectedNewMember(e.target.value)}
                      className="w-full p-2 rounded-lg border border-[var(--border-light)] bg-white dark:bg-[#1a1b1e] text-[var(--text-dark-h)] text-xs outline-none focus:border-primary">
                      <option value="">-- اختر موظف متاح --</option>
                      {teamMembers.map(tm => (
                        <option key={tm.id} value={tm.employee_id}>
                          {tm.employee_name} {tm.active_tasks_count > 0 ? `(${tm.active_tasks_count} مهام)` : '(متاح)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button onClick={handleReassignSubmit} disabled={saving || !selectedNewMember}
                      className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition disabled:opacity-50">
                      تأكيد نقل التعيين
                    </button>
                  </div>
                </div>
              </div>
            )}

            {(!task.assigned_teams || task.assigned_teams.length === 0) ? (
              <p className="text-center text-sm text-[var(--text-muted)] py-4">لم تُعيّن لأي فريق بعد</p>
            ) : task.assigned_teams.map(tt => (
              <div key={tt.id} className={`p-3 rounded-xl border ${
                tt.status === 'won' ? 'border-green-500/50 bg-green-50/50 dark:bg-green-900/10' :
                tt.status === 'lost' ? 'border-red-500/30 bg-red-50/30 dark:bg-red-900/10 opacity-60' :
                'border-[var(--border-light)]'
              }`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="font-bold text-sm text-[var(--text-dark-h)]">🏢 {tt.team_name}</span>
                  <span className="text-xs text-[var(--text-muted)]">({tt.leader_name})</span>
                  {tt.status === 'won' && <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2.5 py-0.5 rounded-full font-bold animate-bounce">🏆 فائز</span>}
                  {tt.status === 'lost' && <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2.5 py-0.5 rounded-full font-bold">❌ خسر</span>}
                  {tt.status === 'in_progress' && <span className="text-[10px] bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400 px-2.5 py-0.5 rounded-full font-bold animate-pulse">⚡ قيد التنفيذ</span>}
                  {tt.status === 'submitted' && <span className="text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400 px-2.5 py-0.5 rounded-full font-bold">🔍 قيد المراجعة</span>}
                  {tt.status === 'assigned' && <span className="text-[10px] bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 px-2.5 py-0.5 rounded-full font-bold">📝 للتنفيذ</span>}
                  
                  {((isTeamLeader && String(user.employee_id) === String(tt.leader_employee_id)) || isSupervisor) && !['ACCEPTED', 'LOST', 'CANCELLED', 'ARCHIVED', 'REJECTED'].includes(task.status) && (
                    <button 
                      onClick={() => setShowDistribute({ task, teamId: tt.team_id })} 
                      className="mr-auto text-xs flex items-center gap-1 text-primary hover:underline font-bold"
                      title="توزيع المهمة على موظفين في هذا الفريق"
                    >
                      <Plus className="w-3.5 h-3.5" /> إضافة تعيين (توزيع)
                    </button>
                  )}
                </div>
                {task.assignments?.filter(a => a.team_id === tt.team_id).length > 0 && (
                  <div className="mr-4 mt-2 space-y-2">
                    {task.assignments.filter(a => a.team_id === tt.team_id).map(a => {
                      const showActions = (isTeamLeader && String(user.employee_id) === String(tt.leader_employee_id)) || isSupervisor;
                      return (
                        <div key={a.id} className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs gap-3">
                          <div className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                            <span className="font-bold text-[var(--text-dark-h)]">{a.employee_name}</span>
                            {a.share_percentage !== null && a.share_percentage !== undefined && (
                              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-bold text-[10px]">
                                {a.share_percentage}% مساهمة
                              </span>
                            )}
                            {a.assigner_name && (
                              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 font-medium text-[10px]" title={`تم التعيين بواسطة: ${a.assigner_name}`}>
                                معيّن بواسطة: {a.assigner_name}
                              </span>
                            )}
                          </div>
                          {showActions && (
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => setReassignFrom({ employee_id: a.employee_id, team_id: tt.team_id, employee_name: a.employee_name })}
                                className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/20 rounded transition" title="إعادة تعيين / نقل">
                                <ArrowLeftRight className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleUnassign(a.employee_id, tt.team_id, a.employee_name)}
                                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded transition" title="إلغاء التعيين">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Tab: Attachments ── */}
        {tab === 'attachments' && (
          <div className="space-y-4">
            {/* Brief attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-xs text-[var(--text-gray)]">📎 مرفقات الاستلام (البريف)</h4>
                {(task.attachments?.filter(a => a.attachment_type === 'brief' && a.file_path) || []).length > 0 && (
                  <a href={getZipDownloadUrl(task.id, 'brief')} className="text-xs flex items-center gap-1 text-primary hover:underline">
                    <Archive className="w-3.5 h-3.5" /> تحميل الكل (ZIP)
                  </a>
                )}
              </div>
              {(task.attachments?.filter(a => a.attachment_type === 'brief') || []).length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">لا توجد مرفقات</p>
              ) : task.attachments.filter(a => a.attachment_type === 'brief').map(a => {
                const isDeleted = !a.file_path;
                const isVideo = !isDeleted && /\.(mp4|webm|mov|ogg|mkv)$/i.test(a.file_name || a.file_path);
                const isImage = !isDeleted && /\.(jpg|jpeg|png|gif|webp)$/i.test(a.file_name || a.file_path);
                return (
                  <div key={a.id} className="mb-2">
                    {isDeleted ? (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-dashed border-gray-200 dark:border-gray-800 text-xs text-gray-400 dark:text-gray-500">
                        <Paperclip className="w-3.5 h-3.5 text-gray-300" />
                        <span className="line-through">{a.file_name}</span>
                        <span className="text-[10px] bg-red-50 text-red-500 dark:bg-red-950/30 dark:text-red-400 px-1.5 py-0.5 rounded mr-auto font-bold">حُذف لتوفير مساحة السيرفر</span>
                      </div>
                    ) : (
                      <>
                        <a href={a.file_path} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 rounded-lg bg-[var(--bg-flat-50)] hover:bg-primary/5 transition text-xs text-primary mb-1">
                          <Download className="w-3.5 h-3.5" /> {a.file_name} <span className="text-[var(--text-muted)] mr-auto">{(a.file_size / 1024).toFixed(0)} KB</span>
                        </a>
                        {isVideo && <video src={a.file_path} controls preload="metadata" className="w-full mt-1 rounded bg-black/5 max-h-64" />}
                        {isImage && <img src={a.file_path} alt={a.file_name} loading="lazy" className="w-full mt-1 rounded bg-black/5 max-h-64 object-contain" />}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Delivery attachments */}
            <div>
              <div className="flex items-center justify-between mb-2 mt-4">
                <h4 className="font-bold text-xs text-[var(--text-gray)]">📎 مرفقات التسليم</h4>
                {(task.attachments?.filter(a => a.attachment_type === 'delivery' && a.file_path) || []).length > 0 && (
                  <a href={getZipDownloadUrl(task.id, 'delivery')} className="text-xs flex items-center gap-1 text-green-600 hover:underline">
                    <Archive className="w-3.5 h-3.5" /> تحميل الكل (ZIP)
                  </a>
                )}
              </div>
              {(task.attachments?.filter(a => a.attachment_type === 'delivery') || []).length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">لا توجد مرفقات تسليم</p>
              ) : task.attachments.filter(a => a.attachment_type === 'delivery').map(a => {
                const isDeleted = !a.file_path;
                const isVideo = !isDeleted && /\.(mp4|webm|mov|ogg|mkv)$/i.test(a.file_name || a.file_path);
                const isImage = !isDeleted && /\.(jpg|jpeg|png|gif|webp)$/i.test(a.file_name || a.file_path);
                return (
                  <div key={a.id} className="mb-2">
                    {isDeleted ? (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-dashed border-gray-200 dark:border-gray-800 text-xs text-gray-400 dark:text-gray-500">
                        <Paperclip className="w-3.5 h-3.5 text-gray-300" />
                        <span className="line-through">{a.file_name}</span>
                        <span className="text-[10px] bg-red-50 text-red-500 dark:bg-red-950/30 dark:text-red-400 px-1.5 py-0.5 rounded mr-auto font-bold">حُذف لتوفير مساحة السيرفر</span>
                      </div>
                    ) : (
                      <>
                        <a href={a.file_path} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/10 hover:bg-green-100 transition text-xs text-green-700 dark:text-green-400 mb-1">
                          <Download className="w-3.5 h-3.5" /> {a.file_name} <span className="text-[var(--text-muted)] mr-auto">{(a.file_size / 1024).toFixed(0)} KB</span>
                        </a>
                        {isVideo && <video src={a.file_path} controls preload="metadata" className="w-full mt-1 rounded bg-black/5 max-h-64" />}
                        {isImage && <img src={a.file_path} alt={a.file_name} loading="lazy" className="w-full mt-1 rounded bg-black/5 max-h-64 object-contain" />}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Tab: Review ── */}
        {tab === 'review' && (
          <div className="space-y-4">
            {/* ═══ Owner/Admin Task Rejection Action ═══ */}
            {isOwner && !['ACCEPTED', 'REJECTED', 'CANCELLED', 'ARCHIVED'].includes(task.status) && (
              <div className="p-3.5 rounded-xl border border-red-500/20 bg-red-500/5 space-y-2 mb-4">
                <p className="text-xs font-bold text-red-600 dark:text-red-400">🚨 إجراء المالك/المدير: رفض المهمة نهائياً</p>
                <textarea 
                  value={rejectNotes} 
                  onChange={e => setRejectNotes(e.target.value)} 
                  placeholder="اكتب سبب الرفض النهائي للمهمة هنا..."
                  className="w-full p-2 rounded-lg border border-red-200 dark:border-red-900/30 bg-[var(--input-bg)] text-[var(--text-dark-h)] text-xs focus:ring-1 focus:ring-red-500 outline-none" 
                  rows={2} 
                />
                <button 
                  onClick={handleRejectTask} 
                  disabled={saving || !rejectNotes.trim()}
                  className="w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  <XCircle className="w-3.5 h-3.5" /> رفض وإرسال للأرشيف ❌
                </button>
              </div>
            )}

            {submissions.length === 0 ? (
              <p className="text-center text-sm text-[var(--text-muted)] py-4">لا توجد تسليمات بعد</p>
            ) : submissions.map(sub => (
              <div key={sub.id} className="p-4 rounded-xl border border-[var(--border-light)] bg-[var(--bg-flat-50)] space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-[var(--text-dark-h)]">{sub.submitter_name}</span>
                    <span className="text-xs text-[var(--text-muted)]">— فريق {sub.team_name}</span>
                  </div>
                  <span className="text-xs text-[var(--text-muted)]">{new Date(sub.submitted_at).toLocaleDateString('ar-IQ')}</span>
                </div>

                {/* Review status — Dual badges (TL + SV) */}
                <div className="flex gap-3 text-xs flex-wrap">
                  {/* TL Badge */}
                  <span className={`px-2.5 py-1 rounded-full font-bold ${
                    sub.tl_status === 'approved' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                    sub.tl_status === 'revision' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                  }`}>
                    {sub.tl_status === 'approved' ? '👑 القائد: موافق' : sub.tl_status === 'revision' ? '🔄 القائد: مطلوب تعديل' : '⏳ بانتظار مراجعة القائد'}
                  </span>
                  {/* SV Badge — only show if TL approved */}
                  {sub.tl_status === 'approved' && (
                    <span className={`px-2.5 py-1 rounded-full font-bold ${
                      sub.sv_status === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      sub.sv_status === 'revision' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                      sub.sv_status === 'rejected' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                      'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    }`}>
                      {sub.sv_status === 'accepted' ? '✅ المتابع: مقبول' : sub.sv_status === 'revision' ? '🔄 المتابع: مطلوب تعديل' : sub.sv_status === 'rejected' ? '❌ المتابع: مرفوض' : '⏳ بانتظار مراجعة المتابع'}
                    </span>
                  )}
                </div>

                {/* TL Notes */}
                {sub.tl_notes && <div className="text-xs p-2 rounded-lg bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-400"><MessageSquare className="w-3 h-3 inline ml-1" /> ملاحظات القائد: {sub.tl_notes}</div>}
                {/* Supervisor Notes */}
                {sub.sv_notes && <div className="text-xs p-2 rounded-lg bg-purple-50 dark:bg-purple-900/10 text-purple-700 dark:text-purple-400"><MessageSquare className="w-3 h-3 inline ml-1" /> ملاحظات المتابع: {sub.sv_notes}</div>}

                {/* Beautiful mobile-optimized interactive attachments view */}
                {sub.files?.length > 0 && (
                  <SubmissionFilePreview files={sub.files} getDownloadUrl={getDownloadUrl} />
                )}

                {/* ═══ TL Review Actions (when tl_status = pending and user is TL) ═══ */}
                {isTeamLeader && sub.tl_status === 'pending' && (
                  <div className="space-y-2 pt-2 border-t border-blue-200 dark:border-blue-800/30">
                    <p className="text-xs font-bold text-blue-600 dark:text-blue-400">👑 مراجعة قائد الفريق</p>
                    <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="ملاحظات القائد..."
                      className="w-full p-2 rounded-lg border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm" rows={2} />
                    <div className="flex gap-2">
                      <button onClick={() => handleTLReview(sub.id, 'approved')} disabled={saving}
                        className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                        <CheckCircle className="w-3.5 h-3.5 inline ml-1" /> موافقة ✓
                      </button>
                      <button onClick={() => handleTLReview(sub.id, 'revision')} disabled={saving}
                        className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                        <RotateCcw className="w-3.5 h-3.5 inline ml-1" /> إعادة بملاحظات
                      </button>
                    </div>
                  </div>
                )}

                {/* ═══ Supervisor Review Actions (Allows direct review/bypass of TL) ═══ */}
                {isSupervisor && sub.sv_status === 'pending' && (
                  <div className="space-y-2 pt-2 border-t border-[var(--border-light)]">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-purple-600 dark:text-purple-400">🔍 مراجعة المتابع</p>
                      {sub.tl_status === 'pending' && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-850 dark:text-yellow-400 font-bold animate-pulse">
                          ⚠️ موافقة القائد معلقة (سيتم التخطي)
                        </span>
                      )}
                    </div>
                    <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="ملاحظات المراجعة..."
                      className="w-full p-2 rounded-lg border border-[var(--border-light)] bg-[var(--input-bg)] text-[var(--text-dark-h)] text-sm" rows={2} />
                    <div className="flex gap-2">
                      <button onClick={() => handleSVReview(sub.id, 'accepted')} disabled={saving}
                        className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                        <CheckCircle className="w-3.5 h-3.5 inline ml-1" /> قبول التسليم ✅
                      </button>
                      <button onClick={() => handleSVReview(sub.id, 'revision')} disabled={saving}
                        className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                        <RotateCcw className="w-3.5 h-3.5 inline ml-1" /> إعادة للتعديل
                      </button>
                      <button onClick={() => setShowTransferUI(showTransferUI === sub.id ? null : sub.id)} disabled={saving}
                        className="py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                        <ArrowLeftRight className="w-3.5 h-3.5 inline ml-1" /> نقل
                      </button>
                    </div>

                    {/* Transfer UI */}
                    {showTransferUI === sub.id && (
                      <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 space-y-2 mt-2">
                        <p className="text-xs font-bold text-red-600">نقل المهمة لفريق آخر</p>
                        <select value={transferTeamId} onChange={e => setTransferTeamId(e.target.value)}
                          className="w-full p-2 rounded-lg border border-[var(--border-light)] bg-[var(--input-bg)] text-sm">
                          <option value="">اختر الفريق الجديد...</option>
                          {teams.filter(t => !task.assigned_teams?.find(at => at.team_id === t.id)).map(t => (
                            <option key={t.id} value={t.id}>{t.name} ({t.leader_name})</option>
                          ))}
                        </select>
                        <p className="text-[10px] text-red-500">⚠️ هذا سيُحسب كفشل على الفريق الحالي</p>
                        <div className="flex gap-2">
                          <button onClick={() => setShowTransferUI(null)} className="flex-1 py-1.5 text-xs border border-[var(--border-light)] rounded-lg hover:bg-[var(--bg-flat-50)]">إلغاء</button>
                          <button onClick={() => handleTransfer(sub.id)} disabled={saving || !transferTeamId}
                            className="flex-1 py-1.5 text-xs bg-red-600 text-white rounded-lg font-bold disabled:opacity-50">نقل المهمة</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Tab: Comments ── */}
        {tab === 'comments' && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 max-h-64 overflow-y-auto custom-scrollbar pr-1">
              {(!task.comments || task.comments.length === 0) ? (
                <p className="text-center text-sm text-[var(--text-muted)] py-4">لا توجد تعليقات بعد</p>
              ) : task.comments.map(c => {
                const isMine = String(c.employee_id) === String(user.employee_id);
                return (
                  <div key={c.id} className={`p-3 rounded-xl max-w-[85%] ${isMine ? 'bg-primary/10 mr-auto ml-2' : 'bg-[var(--bg-flat-50)] border border-[var(--border-light)] ml-auto mr-2'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-xs text-[var(--text-dark-h)]">{c.employee_name}</span>
                      <span className="text-[10px] text-[var(--text-muted)]">{new Date(c.created_at).toLocaleString('ar-IQ')}</span>
                    </div>
                    <p className="text-sm text-[var(--text-dark-h)] whitespace-pre-wrap">{c.content}</p>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <input type="text" value={commentText} onChange={e => setCommentText(e.target.value)}
                placeholder="اكتب تعليقاً..." className="flex-1 p-2.5 rounded-xl border border-[var(--border-light)] bg-[var(--input-bg)] text-sm"
                onKeyDown={e => e.key === 'Enter' && handleAddComment()} />
              <button disabled={saving || !commentText.trim()} onClick={handleAddComment}
                className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold text-sm disabled:opacity-50">
                إرسال
              </button>
            </div>
          </div>
        )}

        {/* ── Tab: Activity ── */}
        {tab === 'activity' && (
          <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
            {(task.activity || []).length === 0 ? (
              <p className="text-center text-sm text-[var(--text-muted)] py-4">لا توجد أحداث</p>
            ) : task.activity.map(a => (
              <div key={a.id} className="flex items-start gap-2 p-2.5 rounded-lg bg-[var(--bg-flat-50)] text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                <div>
                  <span className="font-bold text-[var(--text-dark-h)]">{a.user_name || a.username}</span>
                  <span className="text-[var(--text-muted)] mr-1">— {a.action}</span>
                  <span className="block text-[var(--text-muted)]">{new Date(a.created_at).toLocaleString('ar-IQ')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
